import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { compressImage } from '../utils/compressImage';
import { useLocation } from '../context/LocationContext';
import { useAuth } from '../context/AuthContext';
import {
  FaPlus, FaTrash,
  FaCircle, FaSquare, FaBorderAll, FaImage,
  FaUpload, FaCopy, FaSave, FaCalendarPlus,
} from 'react-icons/fa';

// ─── Constants ────────────────────────────────────────────────────────────────
const PRESET_ZONES = [
  { key: 'indoor',   label: 'İçəri',       color: '#3B82F6', bg: '#EFF6FF' },
  { key: 'outdoor',  label: 'Bayır',       color: '#10B981', bg: '#ECFDF5' },
  { key: 'terrace',  label: 'Terras',      color: '#F59E0B', bg: '#FFFBEB' },
  { key: 'vip',      label: 'VIP',         color: '#8B5CF6', bg: '#F5F3FF' },
  { key: 'bar',      label: 'Bar',         color: '#EF4444', bg: '#FEF2F2' },
  { key: 'banquet',  label: 'Banket Zalı', color: '#0EA5E9', bg: '#F0F9FF' },
];

const CUSTOM_ZONE_COLORS = [
  '#7C3AED','#DB2777','#0284C7','#15803D','#B45309','#9F1239',
  '#0369A1','#047857','#B91C1C','#6D28D9',
];

const ZONE_ICONS = [
  { key: 'home-outline',       emoji: '🏠', label: 'Ev' },
  { key: 'leaf-outline',       emoji: '🌿', label: 'Yaşıl' },
  { key: 'sunny-outline',      emoji: '☀️', label: 'Günəş' },
  { key: 'star-outline',       emoji: '⭐', label: 'Ulduz' },
  { key: 'wine-outline',       emoji: '🍷', label: 'Bar' },
  { key: 'business-outline',   emoji: '🏢', label: 'Bina' },
  { key: 'restaurant-outline', emoji: '🍽️', label: 'Restoran' },
  { key: 'umbrella-outline',   emoji: '☂️', label: 'Çətir' },
  { key: 'people-outline',     emoji: '👥', label: 'Qrup' },
  { key: 'flame-outline',      emoji: '🔥', label: 'Alov' },
  { key: 'cafe-outline',       emoji: '☕', label: 'Kafe' },
  { key: 'water-outline',      emoji: '💧', label: 'Su' },
  { key: 'musical-notes-outline', emoji: '🎵', label: 'Musiqi' },
  { key: 'apps-outline',       emoji: '⊞',  label: 'Ümumi' },
];

const SHAPES = [
  { key: 'square',    label: 'Kvadrat',  icon: <FaSquare /> },
  { key: 'circle',    label: 'Dairə',    icon: <FaCircle /> },
  { key: 'rectangle', label: 'Uzunsov',  icon: <FaBorderAll /> },
];

const STATUS_CFG = {
  available: { label: 'Boş',    color: '#10B981', bg: '#D1FAE5' },
  reserved:  { label: 'Rezerv', color: '#F59E0B', bg: '#FEF3C7' },
  occupied:  { label: 'Dolu',   color: '#EF4444', bg: '#FEE2E2' },
};

const GROUP_COLORS = ['#7C3AED', '#DB2777', '#0284C7', '#15803D', '#B45309', '#9F1239'];

const DEFAULT_TABLE = {
  table_number: '', capacity: 2, shape: 'square', zone: 'indoor',
  pos_x: 10, pos_y: 10, width: 9, height: 9, rotation: 0,
  is_active: true, status: 'available', floor_level: 1,
  group_id: null, occupied_since: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getZone(key, zones) { const zz = zones || PRESET_ZONES; return zz.find(z => z.key === key) || PRESET_ZONES[0]; }

function snapVal(val, enabled, grid = 5) {
  return enabled ? Math.round(val / grid) * grid : val;
}

function formatTimer(ms) {
  if (!ms || ms < 60000) return null;
  const total = Math.floor(ms / 60000);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}s ${m}d` : `${m}d`;
}

function useWindowSize() {
  const [size, setSize] = useState({ w: window.innerWidth });
  useEffect(() => {
    const fn = () => setSize({ w: window.innerWidth });
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return size;
}

// ─── Smart Table Hub (QuickMenu replacement) ──────────────────────────────────
function SmartTableHub({ table, x, y, canvasW, canvasH, onAction, onClose, businessId, onBook, isMobile }) {
  const ref      = useRef(null);
  const [bks, setBks]       = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Close on outside click
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', fn), 0);
    return () => document.removeEventListener('mousedown', fn);
  }, [onClose]);

  // Load today's bookings for this table
  useEffect(() => {
    if (!table.id || table._isNew) { setLoaded(true); return; }
    const today = new Date().toISOString().split('T')[0];
    supabase.from('flash_bookings')
      .select('reservation_time, end_time, contact_name, guest_count, status')
      .eq('table_id', table.id)
      .eq('reservation_date', today)
      .in('status', ['pending','confirmed','active','accepted'])
      .order('reservation_time')
      .then(({ data }) => { setBks(data || []); setLoaded(true); });
  }, [table.id]);

  // Helpers
  const toMin  = s => { const [h,m]=(s||'00:00').slice(0,5).split(':').map(Number); return h*60+m; };
  const toHHMM = m => `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`;

  const now    = new Date();
  const curMin = now.getHours()*60 + now.getMinutes();
  const DAY_START = 360;  // 06:00
  const DAY_END   = 1440; // 24:00
  const SPAN = DAY_END - DAY_START;

  // Next upcoming booking
  const nextBk = bks.find(b => toMin(b.reservation_time) > curMin);
  const minsToNext = nextBk ? toMin(nextBk.reservation_time) - curMin : null;
  const isConflict = minsToNext !== null && minsToNext <= 90;

  // Timeline segments
  const segments = [];
  for (const b of bks) {
    const s = Math.max(toMin(b.reservation_time), DAY_START);
    const e = Math.min(b.end_time ? toMin(b.end_time) : s+120, DAY_END);
    if (e <= DAY_START || s >= DAY_END) continue;
    segments.push({ left: ((s-DAY_START)/SPAN)*100, width: ((e-s)/SPAN)*100, from: s, to: e, b });
  }

  // Now indicator
  const nowPct = Math.max(0, Math.min(100, ((curMin-DAY_START)/SPAN)*100));

  // Position: clamp so hub doesn't go off canvas edges
  const HUB_W = 260;
  const rawLeft = x - HUB_W / 2;
  const clampedLeft = Math.max(4, Math.min((canvasW||800) - HUB_W - 4, rawLeft));
  // Show below table if near top, above otherwise
  const showBelow = y < 140;
  const topStyle  = showBelow ? { top: y + 8 } : { top: y - 8, transform: 'translateY(-100%)' };

  const status = STATUS_CFG[table.status] || STATUS_CFG.available;

  const hubStyle = isMobile
    ? { position:'fixed', bottom:0, left:0, right:0, zIndex:500, background:'#1E293B',
        borderRadius:'16px 16px 0 0', boxShadow:'0 -4px 32px rgba(0,0,0,0.4)',
        border:'none', overflow:'hidden' }
    : { position:'absolute', left:clampedLeft, ...topStyle, zIndex:500, width:HUB_W,
        background:'#1E293B', borderRadius:14,
        boxShadow:'0 12px 40px rgba(0,0,0,0.35)',
        border:'1.5px solid #334155', overflow:'hidden' };

  return (
    <>
      {isMobile && (
        <div onClick={onClose}
          style={{ position:'fixed', inset:0, zIndex:499, background:'rgba(0,0,0,0.3)' }} />
      )}
    <div ref={ref}
      onMouseDown={e => e.stopPropagation()}
      style={hubStyle}>

      {/* ── Mobile pull handle ── */}
      {isMobile && (
        <div style={{ display:'flex', justifyContent:'center', padding:'10px 0 4px' }}>
          <div style={{ width:36, height:4, borderRadius:2, background:'#475569' }} />
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ padding: isMobile ? '6px 16px 8px' : '10px 12px 8px', borderBottom: '1px solid #334155', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          <span style={{ fontSize:14, fontWeight:900, color:'#F8FAFC' }}>Masa {table.table_number}</span>
          <span style={{ fontSize:11, color:'#94A3B8' }}>{table.capacity}🪑</span>
          <span style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:20, background: status.bg, color: status.color }}>
            {status.label}
          </span>
          {table.occupied_since && table.status === 'occupied' && (
            <span style={{ fontSize:10, color:'#EF4444', background:'#450a0a', borderRadius:4, padding:'1px 5px' }}>
              ⏱{formatTimer(Date.now() - new Date(table.occupied_since).getTime()) || '<1d'}
            </span>
          )}
          {table.current_user_name && table.status === 'occupied' && (
            <span style={{ fontSize:10, color:'#93C5FD', background:'rgba(59,130,246,0.15)', borderRadius:4, padding:'1px 6px', maxWidth:90, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              👤 {table.current_user_name}
            </span>
          )}
        </div>
        <button onMouseDown={onClose}
          style={{ background:'none', border:'none', color:'#64748B', cursor:'pointer', fontSize:15, lineHeight:1, padding:0 }}>✕</button>
      </div>

      {/* ── Status Buttons ── */}
      <div style={{ display:'flex', gap:0, padding: isMobile ? '10px 14px' : '8px 10px', borderBottom:'1px solid #334155' }}>
        {Object.entries(STATUS_CFG).map(([key, cfg]) => (
          <button key={key}
            onMouseDown={(e) => { e.stopPropagation(); onAction(table, key); onClose(); }}
            style={{
              flex: 1, padding: isMobile ? '14px 4px' : '8px 4px', border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: isMobile ? 14 : 12, borderRadius: 8, margin: '0 2px',
              background: table.status === key ? cfg.bg : 'rgba(255,255,255,0.06)',
              color:      table.status === key ? cfg.color : '#94A3B8',
              outline:    table.status === key ? `2px solid ${cfg.color}` : 'none',
              transition: 'background 0.15s',
            }}>
            {cfg.label}
          </button>
        ))}
      </div>

      {/* ── Book Action ── */}
      {onBook && (
        <div style={{ padding: isMobile ? '8px 14px' : '6px 10px', borderBottom:'1px solid #334155' }}>
          <button
            onMouseDown={(e) => { e.stopPropagation(); onBook(table); onClose(); }}
            style={{ width:'100%', padding: isMobile ? '13px' : '7px', borderRadius:8, border:'none', cursor:'pointer',
              fontWeight:700, fontSize: isMobile ? 14 : 12, background:'rgba(99,102,241,0.18)', color:'#818CF8' }}>
            📅 Rezervasiya yarat
          </button>
        </div>
      )}

      {/* ── Conflict Warning ── */}
      {isConflict && (
        <div style={{ margin:'8px 10px 0', background:'rgba(251,146,60,0.15)', border:'1.5px solid #F97316', borderRadius:8, padding:'7px 10px' }}>
          <div style={{ fontSize:11, fontWeight:800, color:'#FB923C', marginBottom:2 }}>⚠️ Yaxında rezervasiya var</div>
          <div style={{ fontSize:10, color:'#FED7AA', lineHeight:1.5 }}>
            {minsToNext < 60
              ? `${minsToNext} dəq sonra`
              : `${Math.floor(minsToNext/60)}s ${minsToNext%60}d sonra`}
            {nextBk?.contact_name && ` — ${nextBk.contact_name}`}
            {nextBk?.guest_count && ` (${nextBk.guest_count} nəf)`}
            {' '}üçün rezervdir. Oturtmaqda əminsiniz?
          </div>
        </div>
      )}

      {/* ── Daily Timeline Bar ── */}
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ fontSize:10, color:'#64748B', fontWeight:600, marginBottom:5, display:'flex', justifyContent:'space-between' }}>
          <span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
        </div>

        {/* Bar */}
        <div style={{ position:'relative', height:12, background:'rgba(16,185,129,0.2)', borderRadius:6, overflow:'visible' }}>
          {/* Booked segments */}
          {segments.map((seg, i) => (
            <div key={i} style={{
              position:'absolute', top:0, height:'100%',
              left:`${seg.left}%`, width:`${seg.width}%`,
              background: seg.from <= curMin && seg.to > curMin ? '#EF4444' : '#F59E0B',
              borderRadius: 4,
              title: `${toHHMM(seg.from)}–${toHHMM(seg.to)}`,
            }} />
          ))}
          {/* Now marker */}
          <div style={{ position:'absolute', top:-3, bottom:-3, left:`${nowPct}%`, width:2, background:'#F8FAFC', borderRadius:1, zIndex:2 }} />
        </div>

        {/* Next event line */}
        {loaded && (
          nextBk ? (
            <div style={{ marginTop:7, fontSize:10, color: isConflict ? '#FB923C' : '#94A3B8', display:'flex', alignItems:'center', gap:4 }}>
              <span style={{ fontWeight:700, color: isConflict ? '#FB923C' : '#F59E0B' }}>
                Növbəti: {(nextBk.reservation_time||'').slice(0,5)}
              </span>
              {nextBk.contact_name && <span>— {nextBk.contact_name}</span>}
              {nextBk.guest_count && <span style={{ color:'#64748B' }}>· {nextBk.guest_count} nəf</span>}
              <span style={{ marginLeft:'auto', color:'#475569' }}>
                {minsToNext < 60 ? `${minsToNext}d` : `${Math.floor(minsToNext/60)}s ${minsToNext%60}d`}
              </span>
            </div>
          ) : (
            <div style={{ marginTop:7, fontSize:10, color:'#10B981', fontWeight:600 }}>✓ Bu gün başqa rezervasiya yoxdur</div>
          )
        )}
        {!loaded && <div style={{ marginTop:6, fontSize:10, color:'#475569' }}>⏳ Yüklənir...</div>}
      </div>
    </div>
    </>
  );
}

// ─── TableItem ────────────────────────────────────────────────────────────────
function TableItem({ table, selected, multiSel, onSelect, onDragStart, onTouchDragStart, mode, onStatusChange, scale, groupColor, timerNow, onQuickMenu, hasFutureRes, allZones }) {
  const zone     = getZone(table.zone, allZones);
  const isCircle = table.shape === 'circle';
  const isRect   = table.shape === 'rectangle';

  const status = STATUS_CFG[table.status] || STATUS_CFG.available;

  const isAnySelected = selected || multiSel;

  // Timer: ONLY when status is truly occupied
  const timer = (table.status === 'occupied' && table.occupied_since)
    ? formatTimer(timerNow - new Date(table.occupied_since).getTime())
    : null;

  // ── Colors: status mode → status color, edit mode → zone color ──────────
  const showStatusColor = mode === 'status';

  let bgColor, borderColor, textColor;
  if (showStatusColor) {
    bgColor     = status.bg;
    borderColor = groupColor || status.color;
    textColor   = status.color;
  } else {
    // edit: zone color, selected = filled
    bgColor     = isAnySelected ? (groupColor || zone.color) : (groupColor ? groupColor + '18' : zone.bg);
    borderColor = groupColor || zone.color;
    textColor   = isAnySelected ? '#fff' : (groupColor || zone.color);
  }

  // Future reservation indicator (available now but booked later)
  const showFutureTag = hasFutureRes && table.status === 'available';

  const handleMouseDown = (e) => {
    e.stopPropagation();
    if (mode === 'edit') { onDragStart(e, table.id); onSelect(table.id); }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (mode === 'status' && onQuickMenu) {
      onQuickMenu(table);
    } else if (mode === 'edit') {
      onSelect(table.id);
    }
  };

  const touchStartPos = useRef(null);

  const handleTouchStart = (e) => {
    e.stopPropagation();
    touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    if (mode === 'edit' && onTouchDragStart) {
      onSelect(table.id);
      onTouchDragStart(e.touches[0], table.id);
    }
  };

  const handleTouchEnd = (e) => {
    e.stopPropagation();
    if (mode === 'status' && onQuickMenu) {
      const start = touchStartPos.current;
      const end = e.changedTouches[0];
      const moved = start && Math.hypot(end.clientX - start.x, end.clientY - start.y) > 10;
      if (!moved) onQuickMenu(table);
    }
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={null}
      onMouseLeave={null}
      style={{
        position: 'absolute',
        left: `${table._dpx ?? table.pos_x}%`, top: `${table._dpy ?? table.pos_y}%`,
        width: `${table.width * (isRect ? 1.6 : 1)}%`, height: `${table.height}%`,
        background: bgColor,
        border: `2.5px solid ${borderColor}`,
        borderRadius: isCircle ? '50%' : '8px',
        cursor: mode === 'edit' ? 'grab' : 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        userSelect: 'none',
        boxShadow: isAnySelected
          ? `0 0 0 3px ${borderColor}55, 0 4px 12px rgba(0,0,0,0.18)`
          : groupColor ? `0 0 0 1.5px ${groupColor}50` : '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'background 0.15s, border-color 0.15s',
        animation: 'none',
        zIndex: isAnySelected ? 10 : 1,
        transform: `rotate(${table.rotation || 0}deg)`,
        opacity: table.is_active ? 1 : 0.32,
        minWidth: 34, minHeight: 34,
        overflow: 'visible',
      }}
    >
      {/* Table number */}
      <span style={{ fontSize: Math.max(9, 13*(scale||1)), fontWeight: 800, color: textColor, lineHeight: 1 }}>
        {table.table_number || '?'}
      </span>

      {/* Seat count */}
      <span style={{ fontSize: Math.max(6, 8*(scale||1)), color: showStatusColor ? status.color : '#64748B', lineHeight: 1.3, opacity: 0.85 }}>
        {table.capacity}🪑
      </span>

      {/* Status label — status mode */}
      {mode === 'status' && (
        <span style={{ fontSize: Math.max(6, 7*(scale||1)), fontWeight: 700, color: status.color, lineHeight: 1 }}>
          {status.label}
        </span>
      )}

      {/* Occupied timer */}
      {timer && (
        <span style={{ fontSize: Math.max(6, 7*(scale||1)), fontWeight: 700, color: '#EF4444', background: '#FEE2E2', borderRadius: 3, padding: '1px 3px', lineHeight: 1, marginTop: 1 }}>
          ⏱{timer}
        </span>
      )}

      {/* Scan user name */}
      {table.current_user_name && table.status === 'occupied' && (
        <span style={{ fontSize: Math.max(5, 6*(scale||1)), color: '#3B82F6', background: 'rgba(59,130,246,0.15)', borderRadius: 3, padding: '1px 3px', lineHeight: 1, marginTop: 1, maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          👤{table.current_user_name}
        </span>
      )}

      {/* Future reservation badge — small calendar dot on top-right */}
      {showFutureTag && (
        <span style={{
          position: 'absolute', top: -5, right: -5,
          width: 11, height: 11, borderRadius: '50%',
          background: '#F59E0B', border: '2px solid #fff',
          zIndex: 20,
        }} title="Gün ərzindəki rezervasiyası var" />
      )}
    </div>
  );
}

// ─── Simple Zone View ─────────────────────────────────────────────────────────
function SimpleZoneView({ tables, mode, onStatusChange, onAddTable, onQuickMenu, groupColors, timerNow, allZones }) {
  const zonesArr = allZones || PRESET_ZONES;
  const byZone = {};
  zonesArr.forEach(z => { byZone[z.key] = tables.filter(t => t.zone === z.key && t.is_active); });
  // also catch tables with custom zones not in zonesArr
  tables.forEach(t => { if (t.is_active && !byZone[t.zone]) byZone[t.zone] = []; if (t.is_active && byZone[t.zone] !== undefined && !byZone[t.zone].includes(t)) byZone[t.zone].push(t); });
  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 18 }}>
        {zonesArr.map(z => {
          const zt = byZone[z.key];
          if (!zt.length) return null;
          return (
            <div key={z.key} style={{ background: z.bg, border: `2px solid ${z.color}`, borderRadius: 14, padding: '14px 18px', minWidth: 170 }}>
              <div style={{ fontWeight: 800, color: z.color, marginBottom: 10, fontSize: 14 }}>{z.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {zt.map(t => {
                  const st = STATUS_CFG[t.status] || STATUS_CFG.available;
                  const gc = groupColors?.[t.group_id];
                  const timer = (t.status === 'occupied' && t.occupied_since)
                    ? formatTimer(timerNow - new Date(t.occupied_since).getTime()) : null;
                  return (
                    <div key={t.id}
                      onClick={() => mode === 'status' && (onQuickMenu ? onQuickMenu(t) : onStatusChange(t))}
                      style={{ background: st.bg, border: `2px solid ${gc || st.color}`, borderRadius: 10, padding: '10px 12px', textAlign: 'center', minWidth: 56, cursor: mode==='status'?'pointer':'default', transition:'transform 0.1s', userSelect:'none' }}
                      onTouchStart={e => e.currentTarget.style.transform='scale(0.93)'}
                      onTouchEnd={e => e.currentTarget.style.transform='scale(1)'}
                    >
                      <div style={{ fontWeight: 800, fontSize: 15, color: gc || st.color }}>{t.table_number}</div>
                      <div style={{ fontSize: 11, color: st.color }}>{t.capacity}🪑</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: st.color }}>{st.label}</div>
                      {timer && <div style={{ fontSize: 9, fontWeight: 700, color: '#EF4444' }}>{timer}</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: z.color }}>
                Boş: {zt.filter(t => t.status==='available').length} / {zt.length}
              </div>
            </div>
          );
        })}
      </div>
      {mode === 'edit' && (
        <button onClick={onAddTable} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#E63950', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          <FaPlus /> Masa əlavə et
        </button>
      )}
    </div>
  );
}

// ─── Conflict Modal ───────────────────────────────────────────────────────────
function ConflictModal({ booking, onConfirm, onCancel }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:18, padding:'28px 28px 22px', maxWidth:360, width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize:36, textAlign:'center', marginBottom:12 }}>⚠️</div>
        <h3 style={{ margin:'0 0 10px', fontSize:17, fontWeight:800, color:'#1a1a2e', textAlign:'center' }}>
          Rezervasiya mövcuddur
        </h3>
        <p style={{ margin:'0 0 20px', fontSize:14, color:'#475569', textAlign:'center', lineHeight:1.6 }}>
          Bu masa saat{' '}
          <strong style={{ color:'#F59E0B' }}>{(booking.reservation_time||'').slice(0,5)}</strong>
          {booking.contact_name && <> — <strong>{booking.contact_name}</strong></>}
          {booking.guest_count && <> ({booking.guest_count} nəf)</>}
          {' '}üçün rezervdir.
          <br />Müştərini oturtmaqda əminsiniz?
        </p>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onCancel}
            style={{ flex:1, padding:'11px', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#64748B', fontWeight:700, fontSize:14, cursor:'pointer' }}>
            Ləğv et
          </button>
          <button onClick={onConfirm}
            style={{ flex:1, padding:'11px', borderRadius:10, border:'none', background:'#EF4444', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer' }}>
            Bəli, Oturt
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Daily Agenda View ────────────────────────────────────────────────────────
function AgendaView({ tableId, businessId }) {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tableId || !businessId) { setItems([]); return; }
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    supabase.from('flash_bookings')
      .select('id, reservation_time, end_time, contact_name, guest_count, status')
      .eq('table_id', tableId)
      .eq('reservation_date', today)
      .in('status', ['pending','confirmed','active','accepted'])
      .order('reservation_time')
      .then(({ data }) => { setItems(data || []); setLoading(false); });
  }, [tableId, businessId]);

  if (loading) return <div style={{ padding:'8px 0', fontSize:11, color:'#94A3B8' }}>⏳ Yüklənir...</div>;
  if (!items.length) return (
    <div style={{ padding:'10px', background:'#F8FAFC', borderRadius:8, textAlign:'center', fontSize:11, color:'#94A3B8', marginTop:4 }}>
      Bu gün rezervasiya yoxdur
    </div>
  );

  // Build time blocks from 10:00 to 24:00
  const toMin = s => { const [h,m]=(s||'00:00').slice(0,5).split(':').map(Number); return h*60+m; };
  const toHHMM = min => `${String(Math.floor(min/60)).padStart(2,'0')}:${String(min%60).padStart(2,'0')}`;
  const START = 600; const END = 1440;
  const blocks = [];
  let cursor = START;
  for (const item of items) {
    const s = toMin(item.reservation_time);
    const e = item.end_time ? toMin(item.end_time) : s + 120;
    if (s > cursor) blocks.push({ type:'free', from: cursor, to: s });
    blocks.push({ type:'booked', from: s, to: e, item });
    cursor = Math.max(cursor, e);
  }
  if (cursor < END) blocks.push({ type:'free', from: cursor, to: END });

  return (
    <div style={{ marginTop:6, display:'flex', flexDirection:'column', gap:3, maxHeight:200, overflowY:'auto' }}>
      {blocks.map((b, i) => b.type === 'booked' ? (
        <div key={i} style={{ background:'#FEF3C7', border:'1.5px solid #F59E0B', borderRadius:7, padding:'5px 8px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:11, fontWeight:800, color:'#92400E' }}>
              {toHHMM(b.from)} – {toHHMM(b.to)}
            </span>
            <span style={{ fontSize:10, background:'#FDE68A', color:'#92400E', borderRadius:5, padding:'1px 5px', fontWeight:700 }}>
              Rezerv
            </span>
          </div>
          {b.item.contact_name && <div style={{ fontSize:10, color:'#78350F', marginTop:2 }}>👤 {b.item.contact_name}{b.item.guest_count ? ` · ${b.item.guest_count} nəf` : ''}</div>}
        </div>
      ) : (
        b.to - b.from >= 30 && (
          <div key={i} style={{ background:'#F0FDF4', border:'1.5px dashed #86EFAC', borderRadius:7, padding:'4px 8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:10, color:'#166534', fontWeight:600 }}>{toHHMM(b.from)} – {toHHMM(b.to)}</span>
            <span style={{ fontSize:9, color:'#16a34a', fontWeight:700 }}>✓ Boş — Walk-in</span>
          </div>
        )
      ))}
    </div>
  );
}

// ─── Timeline Slider ──────────────────────────────────────────────────────────
// ─── Properties Panel ─────────────────────────────────────────────────────────
function PropsPanel({ selected, updateField, isMobile, isTablet, groupColor, onDelete, onDuplicate, businessId, onPhotoUpload, photoUploading, allZones }) {
  return (
    <div style={{
      width: isMobile ? '100%' : isTablet ? 182 : 202,
      background: '#fff', borderRadius: 14,
      border: '1.5px solid #F1F5F9',
      padding: 14, flexShrink: 0,
      boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
    }}>
      {groupColor && (
        <div style={{ background: groupColor+'22', border: `1.5px solid ${groupColor}55`, borderRadius: 8, padding: '5px 10px', marginBottom: 10, fontSize: 11, fontWeight: 700, color: groupColor }}>
          🔗 Birləşdirilmiş masa
        </div>
      )}

      <PL>Nömrə / Ad</PL>
      <input style={INP} value={selected.table_number}
        onChange={e => updateField('table_number', e.target.value)} placeholder="1, VIP-1..." />

      <PL>Tutum (nəfər)</PL>
      <input style={INP} type="number" min={1} max={30} value={selected.capacity}
        onChange={e => updateField('capacity', parseInt(e.target.value)||1)} />

      <PL>Ölçü</PL>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
        <button style={SMB} onClick={() => { const v=Math.max(4,(selected.width||9)-1); updateField('width',v); updateField('height',v); }}>−</button>
        <input type="range" min={4} max={22} step={1} value={selected.width||9}
          onChange={e => { const v=parseInt(e.target.value); updateField('width',v); updateField('height',v); }}
          style={{ flex:1, accentColor:'#f97316' }} />
        <button style={SMB} onClick={() => { const v=Math.min(22,(selected.width||9)+1); updateField('width',v); updateField('height',v); }}>+</button>
        <span style={{ fontSize:11, color:'#94A3B8', minWidth:20 }}>{selected.width||9}</span>
      </div>

      <PL>Fırlanma (°)</PL>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
        <input type="range" min={0} max={359} step={5} value={selected.rotation||0}
          onChange={e => updateField('rotation', parseInt(e.target.value))}
          style={{ flex:1, accentColor:'#8B5CF6' }} />
        <span style={{ fontSize:11, color:'#94A3B8', minWidth:28 }}>{selected.rotation||0}°</span>
      </div>

      <PL>Forma</PL>
      <div style={{ display:'flex', gap:5, marginBottom:10 }}>
        {SHAPES.map(sh => (
          <button key={sh.key} onClick={() => updateField('shape', sh.key)} title={sh.label}
            style={{ ...SMB, flex:1, background: selected.shape===sh.key ? '#1a1a2e' : '#F1F5F9', color: selected.shape===sh.key ? '#fff' : '#64748B', border:`1.5px solid ${selected.shape===sh.key?'#1a1a2e':'#E5E7EB'}` }}>
            {sh.icon}
          </button>
        ))}
      </div>

      <PL>Zona</PL>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4, marginBottom:10 }}>
        {(allZones || PRESET_ZONES).map(z => (
          <button key={z.key} onClick={() => updateField('zone', z.key)}
            style={{ padding:'5px 4px', borderRadius:7, cursor:'pointer', fontWeight:600, fontSize:10, textAlign:'center',
              background: selected.zone===z.key ? z.color : z.bg,
              color: selected.zone===z.key ? '#fff' : z.color,
              border:`1px solid ${z.color}` }}>
            {z.label}
          </button>
        ))}
      </div>

      <PL>Status</PL>
      <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:12 }}>
        {Object.entries(STATUS_CFG).map(([k,v]) => (
          <button key={k} onClick={() => updateField('status', k)}
            style={{ padding:'6px 10px', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:12, textAlign:'left',
              background: selected.status===k ? v.bg : '#F8FAFC',
              color: selected.status===k ? v.color : '#94A3B8',
              border:`1.5px solid ${selected.status===k ? v.color : '#E2E8F0'}` }}>
            ● {v.label}
          </button>
        ))}
      </div>

      <PL>Aktivlik</PL>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <span style={{ fontSize:12, color:'#64748B' }}>{selected.is_active?'Aktiv':'Deaktiv'}</span>
        <div onClick={() => updateField('is_active', !selected.is_active)}
          style={{ width:44, height:24, borderRadius:12, cursor:'pointer', position:'relative', background: selected.is_active?'#10B981':'#CBD5E1', transition:'background 0.2s' }}>
          <div style={{ position:'absolute', top:2, left:2, width:20, height:20, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.2)', transform: selected.is_active?'translateX(20px)':'translateX(0)', transition:'transform 0.2s' }} />
        </div>
      </div>

      <PL>Masa Şəkli</PL>
      {selected.photo_url && (
        <div style={{ position:'relative', marginBottom:6 }}>
          <img src={selected.photo_url} alt="masa" style={{ width:'100%', borderRadius:8, objectFit:'cover', maxHeight:90 }} />
          <button onClick={() => onPhotoUpload(null)}
            style={{ position:'absolute', top:4, right:4, width:20, height:20, borderRadius:'50%', border:'none', background:'rgba(0,0,0,0.55)', color:'#fff', fontSize:11, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>✕</button>
        </div>
      )}
      <label style={{ display:'block', cursor:'pointer' }}>
        <div style={{ padding:'6px 10px', borderRadius:8, border:'1.5px dashed #CBD5E1', background:'#F8FAFC', textAlign:'center', fontSize:11, color:'#64748B', fontWeight:600, marginBottom:10 }}>
          {photoUploading ? '⏳ Yüklənir...' : <><FaUpload style={{ marginRight:4, verticalAlign:'middle' }}/>Şəkil yüklə</>}
        </div>
        <input type="file" accept="image/*" style={{ display:'none' }} disabled={photoUploading} onChange={e => { if(e.target.files[0]) onPhotoUpload(e.target.files[0]); e.target.value=''; }} />
      </label>

      {(onDuplicate||onDelete) && (
        <div style={{ display:'flex', gap:6 }}>
          {onDuplicate && <button onClick={onDuplicate} style={{ flex:1, padding:'7px', borderRadius:8, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, fontSize:12, cursor:'pointer' }}>📋 Kopyala</button>}
          {onDelete    && <button onClick={onDelete}    style={{ flex:1, padding:'7px', borderRadius:8, border:'none', background:'#FEF2F2', color:'#EF4444', fontWeight:600, fontSize:12, cursor:'pointer' }}>🗑️ Sil</button>}
        </div>
      )}

      {/* ── Daily Agenda ── */}
      {!selected._isNew && (
        <>
          <div style={{ borderTop:'1.5px solid #F1F5F9', margin:'12px 0 8px' }} />
          <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#64748B', marginBottom:4 }}>📅 Bu günkü cədvəl</label>
          <AgendaView tableId={selected.id} businessId={businessId} />
        </>
      )}
    </div>
  );
}

const PL  = ({ children }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#64748B', marginBottom:4 }}>{children}</label>;
const INP = { display:'block', width:'100%', padding:'7px 10px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, marginBottom:10, background:'#FAFAFA', boxSizing:'border-box', outline:'none' };
const SMB = { width:28, height:28, borderRadius:7, border:'1.5px solid #E5E7EB', background:'#F1F5F9', color:'#374151', cursor:'pointer', fontSize:13, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 };

// ─── Main FloorPlanPage ───────────────────────────────────────────────────────
export default function FloorPlanPage() {
  const { activeLocation } = useLocation();
  const { business }       = useAuth();
  const wrapRef   = useRef(null);
  const canvasRef = useRef(null);
  const { w: winW } = useWindowSize();
  const isMobile = winW < 768;
  const isTablet = winW >= 768 && winW < 1100;

  // ── Core state ──────────────────────────────────────────────────────────────
  const [tables,      setTables]      = useState([]);
  const [selectedId,  setSelectedId]  = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [mode,        setMode]        = useState('status');
  const [saving,      setSaving]      = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [saved,       setSaved]       = useState(false);
  const [nextNum,     setNextNum]     = useState(1);
  const [customZones,  setCustomZones]  = useState([]);
  const [zoneImages,    setZoneImages]    = useState({});
  const [zoneUploading, setZoneUploading] = useState(null);
  const [zoneFilter,    setZoneFilter]    = useState(null); // null=all, key=only that zone on canvas
  const [showZoneMgr,   setShowZoneMgr]   = useState(false);
  const [newZoneName,  setNewZoneName]  = useState('');
  const [newZoneColor, setNewZoneColor] = useState(CUSTOM_ZONE_COLORS[0]);
  const [newZoneIcon,  setNewZoneIcon]  = useState('apps-outline');
  const [uploading,      setUploading]      = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [autoMode,    setAutoMode]    = useState(() => localStorage.getItem('fp_automode') === '1');
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [showProps,     setShowProps]     = useState(false);
  const [quickMenu,     setQuickMenu]     = useState(null);
  const [showPlanModes, setShowPlanModes] = useState(false);

  // Auto-open PropsPanel on mobile when a table is selected in edit mode
  useEffect(() => {
    if (isMobile && mode === 'edit' && selectedId) setShowProps(true);
    if (!selectedId) setShowProps(false);
  }, [selectedId, isMobile, mode]);

  // ── Bookings ──────────────────────────────────────────────────────────────────
  const [allBookings, setAllBookings] = useState([]);   // today's all bookings

  // ── Conflict modal ────────────────────────────────────────────────────────────
  const [conflictModal, setConflictModal] = useState(null);     // { table, booking, nextStatus }

  // ── Manual booking modal ──────────────────────────────────────────────────────
  const [showBookingModal,  setShowBookingModal]  = useState(false);
  const [bookingForm,       setBookingForm]       = useState({});
  const [bookingSaving,     setBookingSaving]     = useState(false);
  const [confirmResetAll,   setConfirmResetAll]   = useState(false);

  // ── Multi-floor ─────────────────────────────────────────────────────────────
  const [currentLevel, setCurrentLevel] = useState(1);
  const [levelImages,  setLevelImages]  = useState({});
  const [levelNames,   setLevelNames]   = useState({ 1: '1-ci Mərtəbə' });
  const [planModes,    setPlanModes]    = useState({ 1: 'editor' });
  const [imageOpacity, setImageOpacity] = useState(1);

  // ── Zoom / Pan ──────────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(1);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const panRef  = useRef({ x: 0, y: 0 });
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current  = pan;  }, [pan]);

  // ── Editor tools ────────────────────────────────────────────────────────────
  const [gridSnap, setGridSnap] = useState(false);

  // ── Drag / box-select refs ───────────────────────────────────────────────────
  const dragging    = useRef(null);
  const panDrag     = useRef(null);
  const selBoxStart = useRef(null);
  const [selBox, setSelBox] = useState(null);
  const touchRef    = useRef({ type: null });

  // ── Timer (refresh every 30s) ───────────────────────────────────────────────
  const [timerNow, setTimerNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setTimerNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // ── Auto-sync ref ────────────────────────────────────────────────────────────
  const autoRef = useRef(null);
  // Stores the planMode that was active before a zone tab was selected,
  // so switching back to Ümumi restores the exact mode (not guessed from levelImages).
  const prevLevelPlanModeRef = useRef(null);

  // ── Group color map ──────────────────────────────────────────────────────────
  const groupColorMap = useMemo(() => {
    const ids = [...new Set(tables.map(t => t.group_id).filter(Boolean))];
    return Object.fromEntries(ids.map((id, i) => [id, GROUP_COLORS[i % GROUP_COLORS.length]]));
  }, [tables]);

  // ── All zones (preset + custom) ──────────────────────────────────────────────
  const allZones = useMemo(() => {
    const customBg = (color) => color + '18';
    return [
      ...PRESET_ZONES,
      ...customZones.map(cz => ({ ...cz, bg: cz.bg || customBg(cz.color) })),
    ];
  }, [customZones]);

  // ── Future-reservation badge: which tables have a booking later today ─────────
  const futureResIds = useMemo(() => {
    const now = new Date();
    const curMin = now.getHours()*60 + now.getMinutes();
    const toMin = s => { const [h,m]=(s||'00:00').slice(0,5).split(':').map(Number); return h*60+m; };
    return new Set(allBookings.filter(b => toMin(b.reservation_time) > curMin).map(b => b.table_id));
  }, [allBookings, timerNow]);

  // ── Derived: current level's tables (zone filter applied if active) ──────────
  const levelTables = useMemo(() => tables.filter(t => {
    if ((t.floor_level || 1) !== currentLevel) return false;
    if (zoneFilter) return t.zone === zoneFilter;
    return true;
  }), [tables, currentLevel, zoneFilter]);
  const currentPlanMode = planModes[currentLevel] || 'editor';
  const currentImage = zoneFilter
    ? (zoneImages[zoneFilter] || null)
    : (levelImages[currentLevel] || null);

  // When zone filter active → use zone_pos_x/y (separate from main canvas pos)
  const displayTables = useMemo(() => {
    if (!zoneFilter) return levelTables.map(t => ({ ...t, _dpx: t.pos_x, _dpy: t.pos_y }));
    return levelTables.map((t, i) => ({
      ...t,
      _dpx: t.zone_pos_x ?? (5 + (i % 8) * 11),
      _dpy: t.zone_pos_y ?? (5 + Math.floor(i / 8) * 15),
    }));
  }, [levelTables, zoneFilter]);

  const allLevels = useMemo(() => {
    const s = new Set(tables.map(t => t.floor_level || 1));
    s.add(1);
    return [...s].sort((a, b) => a - b);
  }, [tables]);

  // ── Stats ────────────────────────────────────────────────────────────────────
  const activeTables   = levelTables.filter(t => t.is_active);
  const totalSeats     = activeTables.reduce((s, t) => s + (t.capacity || 0), 0);
  const availableCount = activeTables.filter(t => t.status === 'available').length;
  const occupiedCount  = activeTables.filter(t => t.status === 'occupied').length;
  const reservedCount  = activeTables.filter(t => t.status === 'reserved').length;
  const scale          = (canvasRef.current?.offsetWidth || 800) / 800;
  const selected       = tables.find(t => t.id === selectedId);

  // ── Canvas height ────────────────────────────────────────────────────────────
  const canvasH = isMobile ? Math.round(window.innerHeight * 0.58) : isTablet ? 480 : 580;

  // ── Load ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!business?.id) { setLoading(false); return; }
    loadAll();
  }, [business?.id, activeLocation?.id]);

  // ── Realtime: tables ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!business?.id) return;
    const ch = supabase.channel('fp-rt-' + business.id)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tables', filter: `business_id=eq.${business.id}` },
        p => setTables(prev => prev.map(t => t.id === p.new.id ? { ...t, ...p.new } : t))
      ).subscribe();
    return () => supabase.removeChannel(ch);
  }, [business?.id]);

  // ── Realtime: flash_bookings → allBookings live sync ─────────────────────────
  useEffect(() => {
    if (!business?.id) return;
    const ACTIVE = ['pending','confirmed','active','accepted'];
    const today  = new Date().toISOString().split('T')[0];

    const ch = supabase.channel('fp-bk-' + business.id)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'flash_bookings',
        filter: `business_id=eq.${business.id}`,
      }, p => {
        const rec    = p.new || p.old;
        const recId  = rec?.id;
        if (!recId) return;
        // Only care about today's bookings
        const recDate = (p.new?.reservation_date) || (p.old?.reservation_date);
        if (recDate && recDate !== today) return;

        if (p.eventType === 'DELETE' || !ACTIVE.includes(p.new?.status)) {
          // Remove from allBookings
          setAllBookings(prev => prev.filter(b => b.id !== recId));
        } else {
          // Upsert into allBookings
          setAllBookings(prev => {
            const exists = prev.some(b => b.id === recId);
            if (exists) return prev.map(b => b.id === recId ? { ...b, ...p.new } : b);
            return [...prev, p.new];
          });
        }
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [business?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      if (activeLocation?.id) {
        const { data: loc } = await supabase
          .from('business_locations')
          .select('plan_mode, plan_image_url, floor_level_images, floor_level_names, custom_zones, zone_images')
          .eq('id', activeLocation.id)
          .maybeSingle();
        if (loc) {
          const imgs = { ...(loc.floor_level_images || {}) };
          if (!imgs[1] && loc.plan_image_url) imgs[1] = loc.plan_image_url;
          setLevelImages(imgs);
          setLevelNames(loc.floor_level_names || { 1: '1-ci Mərtəbə' });
          setCustomZones(loc.custom_zones || []);
          setZoneImages(loc.zone_images || {});
          const pm = {};
          if (typeof loc.plan_mode === 'string') pm[1] = loc.plan_mode;
          else if (loc.plan_mode && typeof loc.plan_mode === 'object') Object.assign(pm, loc.plan_mode);
          // If a level has an image, always use 'image' mode (unless 'simple' was saved)
          const finalImgs = { ...(loc.floor_level_images || {}) };
          if (!finalImgs[1] && loc.plan_image_url) finalImgs[1] = loc.plan_image_url;
          Object.keys(finalImgs).forEach(lvl => {
            if (finalImgs[lvl] && pm[lvl] !== 'simple') pm[lvl] = 'image';
          });
          setPlanModes(prev => ({ ...prev, ...pm }));
        }
      }
      let q = supabase.from('tables').select('*').eq('business_id', business.id);
      if (activeLocation?.id) q = q.eq('business_location_id', activeLocation.id);
      const { data, error } = await q.order('created_at');
      if (error) { setTables([]); setLoading(false); return; }
      setTables(data || []);
      if (data?.length) {
        const nums = data.map(t => parseInt(t.table_number)).filter(n => !isNaN(n));
        setNextNum(nums.length ? Math.max(...nums) + 1 : 1);
      }
      // Load today's all bookings for timeline + conflict check
      const today = new Date().toISOString().split('T')[0];
      const { data: bkData } = await supabase.from('flash_bookings')
        .select('id, table_id, reservation_time, end_time, contact_name, guest_count, status')
        .eq('business_id', business.id)
        .eq('reservation_date', today)
        .in('status', ['pending','confirmed','active','accepted']);
      setAllBookings(bkData || []);
    } catch { setTables([]); }
    setLoading(false);
  };

  // ── Mobile: force simple view for easier touch use ───────────────────────────
  useEffect(() => {
    if (isMobile) setPlanModes(prev => ({ ...prev, [currentLevel]: 'simple' }));
  }, [isMobile, currentLevel]);

  // ── Save plan mode ────────────────────────────────────────────────────────────
  const savePlanMode = async (pm) => {
    const updated = { ...planModes, [currentLevel]: pm };
    setPlanModes(updated);
    if (activeLocation?.id)
      await supabase.from('business_locations').update({ plan_mode: updated }).eq('id', activeLocation.id);
  };

  // ── Custom zones ──────────────────────────────────────────────────────────────
  const saveCustomZones = async (newCZ) => {
    setCustomZones(newCZ);
    if (activeLocation?.id)
      await supabase.from('business_locations').update({ custom_zones: newCZ }).eq('id', activeLocation.id);
  };

  const addCustomZone = () => {
    const name = newZoneName.trim();
    if (!name) return;
    const key = 'custom_' + Date.now();
    const newCZ = [...customZones, { key, label: name, color: newZoneColor, bg: newZoneColor + '18', icon: newZoneIcon }];
    saveCustomZones(newCZ);
    setNewZoneName('');
  };

  const deleteCustomZone = (key) => {
    saveCustomZones(customZones.filter(z => z.key !== key));
  };

  // ── Zone image upload ─────────────────────────────────────────────────────────
  const handleZoneImageUpload = async (zoneKey, file) => {
    if (!file || !business?.id) return;
    setZoneUploading(zoneKey);
    try {
      const compressed = await compressImage(file, { maxWidth: 2000, quality: 0.90 });
      const path = `floor-plans/${business.id}/${activeLocation?.id || 'main'}_zone_${zoneKey}.jpg`;
      await supabase.storage.from('business-photos').upload(path, compressed, { upsert: true, contentType: 'image/jpeg' });
      const { data: { publicUrl } } = supabase.storage.from('business-photos').getPublicUrl(path);
      const bustedUrl = `${publicUrl}?t=${Date.now()}`;
      const newZI = { ...zoneImages, [zoneKey]: bustedUrl };
      setZoneImages(newZI);
      if (activeLocation?.id)
        await supabase.from('business_locations').update({ zone_images: newZI }).eq('id', activeLocation.id);
    } catch (err) { alert('Yükləmə xətası: ' + err.message); }
    setZoneUploading(null);
  };

  const handleZoneImageRemove = async (zoneKey) => {
    const newZI = { ...zoneImages };
    delete newZI[zoneKey];
    setZoneImages(newZI);
    if (activeLocation?.id)
      await supabase.from('business_locations').update({ zone_images: newZI }).eq('id', activeLocation.id);
  };

  // ── Image upload ──────────────────────────────────────────────────────────────
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 2000, quality: 0.90 });
      const path = `floor-plans/${business.id}/${activeLocation?.id || 'main'}_lv${currentLevel}.jpg`;
      await supabase.storage.from('business-photos').upload(path, compressed, { upsert: true, contentType: 'image/jpeg' });
      const { data: { publicUrl } } = supabase.storage.from('business-photos').getPublicUrl(path);
      const bustedUrl = `${publicUrl}?t=${Date.now()}`;
      const newImgs = { ...levelImages, [currentLevel]: bustedUrl };
      setLevelImages(newImgs);
      if (activeLocation?.id)
        await supabase.from('business_locations').update({ floor_level_images: newImgs, plan_image_url: bustedUrl }).eq('id', activeLocation.id);
      savePlanMode('image');
    } catch (err) { alert('Upload xəta: ' + err.message); }
    setUploading(false);
  };

  const handleImageRemove = async () => {
    const newImgs = { ...levelImages };
    delete newImgs[currentLevel];
    setLevelImages(newImgs);
    if (activeLocation?.id) {
      const remaining = Object.values(newImgs)[0] || null;
      await supabase.from('business_locations').update({ floor_level_images: newImgs, plan_image_url: remaining }).eq('id', activeLocation.id);
    }
    savePlanMode('editor');
  };

  // ── Add table ─────────────────────────────────────────────────────────────────
  const addTable = (px, py) => {
    const num = nextNum; setNextNum(n => n + 1);
    const t = {
      ...DEFAULT_TABLE, id: `temp_${Date.now()}`, table_number: String(num),
      pos_x: snapVal(px ?? 5 + (levelTables.length % 8) * 11, gridSnap),
      pos_y: snapVal(py ?? 5 + Math.floor(levelTables.length / 8) * 15, gridSnap),
      business_id: business?.id, business_location_id: activeLocation?.id || null,
      floor_level: currentLevel, _isNew: true,
      zone: zoneFilter || DEFAULT_TABLE.zone,
    };
    setTables(prev => [...prev, t]);
    setSelectedId(t.id); setSelectedIds([]);
  };

  // ── Duplicate ─────────────────────────────────────────────────────────────────
  const duplicateTable = useCallback(() => {
    const t = tables.find(x => x.id === selectedId); if (!t) return;
    const num = nextNum; setNextNum(n => n + 1);
    const copy = { ...t, id: `temp_${Date.now()}`, table_number: String(num),
      pos_x: Math.min(88, (t.pos_x||0)+3), pos_y: Math.min(88, (t.pos_y||0)+3),
      _isNew: true, group_id: null, occupied_since: null, status: 'available' };
    setTables(prev => [...prev, copy]);
    setSelectedId(copy.id);
  }, [tables, selectedId, nextNum]);

  // ── Delete ────────────────────────────────────────────────────────────────────
  const deleteTable = useCallback(async () => {
    const ids = selectedIds.length > 0 ? selectedIds : (selectedId ? [selectedId] : []);
    if (!ids.length) return;
    for (const id of ids) {
      const t = tables.find(x => x.id === id);
      if (t && !t._isNew) await supabase.from('tables').delete().eq('id', t.id);
    }
    setTables(prev => prev.filter(t => !ids.includes(t.id)));
    setSelectedId(null); setSelectedIds([]);
  }, [tables, selectedId, selectedIds]);

  // ── Update field ──────────────────────────────────────────────────────────────
  const updateField = (field, value) => {
    setTables(prev => prev.map(t => t.id === selectedId ? { ...t, [field]: value } : t));
  };

  const handleTablePhotoUpload = async (file) => {
    const table = tables.find(t => t.id === selectedId);
    if (!table || table._isNew) { alert('Əvvəlcə masanı saxlayın.'); return; }
    if (file === null) {
      await supabase.from('tables').update({ photo_url: null }).eq('id', table.id);
      setTables(prev => prev.map(t => t.id === table.id ? { ...t, photo_url: null } : t));
      return;
    }
    setPhotoUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 800, quality: 0.82 });
      const path = `table-photos/${business.id}/${table.id}.jpg`;
      await supabase.storage.from('business-photos').upload(path, compressed, { upsert: true, contentType: 'image/jpeg' });
      const { data: { publicUrl } } = supabase.storage.from('business-photos').getPublicUrl(path);
      await supabase.from('tables').update({ photo_url: publicUrl }).eq('id', table.id);
      setTables(prev => prev.map(t => t.id === table.id ? { ...t, photo_url: publicUrl } : t));
    } catch (e) { alert('Şəkil yüklənmədi: ' + e.message); }
    setPhotoUploading(false);
  };

  // ── Status change (conflict check + group sync + timer) ──────────────────────
  const applyStatusChange = useCallback(async (table, next) => {
    const occupied_since = next === 'occupied' ? new Date().toISOString() : null;
    const clearUser = next !== 'occupied';
    const group = table.group_id ? tables.filter(t => t.group_id === table.group_id) : [table];
    setTables(prev => prev.map(t =>
      group.find(g => g.id === t.id)
        ? { ...t, status: next, occupied_since, ...(clearUser ? { current_user_id: null, current_user_name: null } : {}) }
        : t
    ));
    for (const g of group.filter(t => !t._isNew)) {
      const upd = { status: next, occupied_since };
      if (clearUser) { upd.current_user_id = null; upd.current_user_name = null; }
      await supabase.from('tables').update(upd).eq('id', g.id);
    }
  }, [tables]);

  const handleStatusChange = useCallback(async (table, forceStatus) => {
    const cycle = { available: 'occupied', occupied: 'reserved', reserved: 'available' };
    const next = forceStatus || cycle[table.status] || 'available';

    // Smart Conflict: next=occupied → rezervasiya 90 dəq ərzindədir?
    if (next === 'occupied') {
      const now = new Date();
      const toMin = s => { const [h,m]=(s||'00:00').slice(0,5).split(':').map(Number); return h*60+m; };
      const curMin = now.getHours()*60 + now.getMinutes();
      const conflict = allBookings.find(b => {
        if (b.table_id !== table.id) return false;
        const start = toMin(b.reservation_time);
        return start > curMin && start - curMin <= 90;
      });
      if (conflict) {
        setConflictModal({ table, booking: conflict, nextStatus: next });
        return;
      }
    }
    await applyStatusChange(table, next);
  }, [tables, allBookings, applyStatusChange]);

  // ── Quick menu ────────────────────────────────────────────────────────────────
  const handleQuickMenu = useCallback((table) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cw = canvas.offsetWidth;
    const ch = canvas.offsetHeight;
    const tableW = (table.width || 9) * (table.shape === 'rectangle' ? 1.6 : 1);
    const x = (table._dpx ?? table.pos_x ?? 0) / 100 * cw + tableW / 100 * cw / 2;
    const y = (table._dpy ?? table.pos_y ?? 0) / 100 * ch;
    setQuickMenu({ table, x, y });
  }, []);

  // ── Merge / Unmerge ───────────────────────────────────────────────────────────
  const mergeTables = async () => {
    if (selectedIds.length < 2) return;
    const gid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    setTables(prev => prev.map(t => selectedIds.includes(t.id) ? { ...t, group_id: gid } : t));
    for (const id of selectedIds) {
      const t = tables.find(x => x.id === id);
      if (t && !t._isNew) await supabase.from('tables').update({ group_id: gid }).eq('id', id);
    }
    setSelectedIds([]);
  };

  const unmergeTables = async () => {
    const ids = selectedIds.length ? selectedIds : (selectedId ? [selectedId] : []);
    setTables(prev => prev.map(t => ids.includes(t.id) ? { ...t, group_id: null } : t));
    for (const id of ids) {
      const t = tables.find(x => x.id === id);
      if (t && !t._isNew) await supabase.from('tables').update({ group_id: null }).eq('id', id);
    }
  };

  // ── Reset all available ───────────────────────────────────────────────────────
  const resetAllAvailable = async () => {
    const ids = levelTables.filter(t => !t._isNew && t.status !== 'available').map(t => t.id);
    setTables(prev => prev.map(t => (t.floor_level||1) === currentLevel ? { ...t, status: 'available', occupied_since: null } : t));
    if (ids.length) await supabase.from('tables').update({ status: 'available', occupied_since: null }).in('id', ids);
  };

  // ── Manual booking ────────────────────────────────────────────────────────────
  const openBookingModal = useCallback((table = null) => {
    const now   = new Date();
    const hh    = String(now.getHours()).padStart(2,'0');
    const mm    = String(now.getMinutes() >= 30 ? 0 : 30).padStart(2,'0');
    const endHH = String(now.getHours() + (now.getMinutes() >= 30 ? 2 : 1)).padStart(2,'0');
    setBookingForm({
      table_id:         table?.id   || '',
      contact_name:       '',
      phone:            '',
      reservation_date: now.toISOString().split('T')[0],
      reservation_time: `${hh}:${mm}`,
      end_time:         `${endHH}:${mm}`,
      guest_count:     table?.capacity || 2,
      note:             '',
    });
    setShowBookingModal(true);
  }, []);

  const submitBooking = useCallback(async () => {
    if (!bookingForm.contact_name?.trim() || !bookingForm.table_id) return;
    setBookingSaving(true);
    try {
      await supabase.from('flash_bookings').insert({
        business_id:      business.id,
        table_id:         bookingForm.table_id,
        contact_name:       bookingForm.contact_name.trim(),
        contact_phone:    bookingForm.phone?.trim() || null,
        reservation_date: bookingForm.reservation_date,
        reservation_time: bookingForm.reservation_time,
        end_time:         bookingForm.end_time || null,
        guest_count:     parseInt(bookingForm.guest_count) || 2,
        notes:            bookingForm.note?.trim() || null,
        status:           'accepted',
        source:           'business_panel',
      });
      setShowBookingModal(false);
    } catch(e) { console.error(e); }
    setBookingSaving(false);
  }, [bookingForm, business?.id]);

  // ── Auto-sync ─────────────────────────────────────────────────────────────────
  const syncFromBookings = useCallback(async () => {
    if (!business?.id) return;
    setAutoSyncing(true);
    try {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const cur = now.getHours()*60 + now.getMinutes();
      const toMin = s => { const [h,m]=(s||'00:00').slice(0,5).split(':').map(Number); return h*60+m; };
      const { data: bks } = await supabase.from('flash_bookings').select('table_id, reservation_time, end_time')
        .eq('business_id', business.id).eq('reservation_date', today).in('status', ['confirmed','active','accepted']);
      const occ = new Set(); const res = new Set();
      (bks||[]).forEach(b => {
        const st = toMin(b.reservation_time); const en = b.end_time ? toMin(b.end_time) : st+120;
        if (cur >= st && cur < en) occ.add(b.table_id);
        else if (cur < st && st-cur <= 30) res.add(b.table_id);
      });
      setTables(prev => prev.map(t => {
        if (t._isNew) return t;
        const next = occ.has(t.id) ? 'occupied' : res.has(t.id) ? 'reserved' : 'available';
        if (t.status !== next) {
          const occupied_since = next==='occupied' ? new Date().toISOString() : null;
          supabase.from('tables').update({ status: next, occupied_since }).eq('id', t.id);
          return { ...t, status: next, occupied_since };
        }
        return t;
      }));
    } catch {}
    setAutoSyncing(false);
  }, [business?.id]);

  useEffect(() => {
    localStorage.setItem('fp_automode', autoMode ? '1' : '0');
    clearInterval(autoRef.current);
    if (!autoMode) return;
    syncFromBookings();
    autoRef.current = setInterval(syncFromBookings, 60000);
    return () => clearInterval(autoRef.current);
  }, [autoMode, syncFromBookings]);


  // ── Save ──────────────────────────────────────────────────────────────────────
  const saveAll = async () => {
    setSaving(true);
    try {
      for (const t of tables) {
        const payload = {
          table_number: t.table_number, capacity: t.capacity, shape: t.shape,
          zone: t.zone, pos_x: t.pos_x, pos_y: t.pos_y, width: t.width, height: t.height,
          rotation: t.rotation||0, is_active: t.is_active, status: t.status||'available',
          tags: t.tags||[], group_id: t.group_id||null,
          occupied_since: t.occupied_since||null, floor_level: t.floor_level||1,
          photo_url: t.photo_url||null,
          zone_pos_x: t.zone_pos_x ?? null, zone_pos_y: t.zone_pos_y ?? null,
          business_id: business.id, business_location_id: activeLocation?.id||null,
        };
        if (t._isNew) {
          const { data, error } = await supabase.from('tables').insert(payload).select().single();
          if (error) throw error;
          setTables(prev => prev.map(x => x.id===t.id ? { ...data } : x));
        } else {
          await supabase.from('tables').update(payload).eq('id', t.id);
        }
      }
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { alert('Xəta: ' + e.message); }
    setSaving(false);
  };

  // ── Wheel zoom ────────────────────────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect = wrapRef.current?.getBoundingClientRect(); if (!rect) return;
    const z = zoomRef.current;
    const newZoom = Math.max(0.25, Math.min(5, z * (e.deltaY > 0 ? 0.9 : 1.1)));
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const p = panRef.current;
    const ix = (cx - p.x) / z;
    const iy = (cy - p.y) / z;
    setZoom(newZoom);
    setPan({ x: cx - ix * newZoom, y: cy - iy * newZoom });
  }, []);

  useEffect(() => {
    const el = wrapRef.current; if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const resetZoom = () => { setZoom(1); setPan({ x:0, y:0 }); };

  // ── Mouse move ────────────────────────────────────────────────────────────────
  const handleMouseMove = useCallback((e) => {
    const z = zoomRef.current;
    const rect = wrapRef.current?.getBoundingClientRect();

    // Table drag
    if (dragging.current) {
      const { startX, startY, ids, origPositions, isZoneMode } = dragging.current;
      if (!rect) return;
      const innerW = rect.width;
      const innerH = rect.height;
      const dxPct = ((e.clientX - startX) / z / innerW) * 100;
      const dyPct = ((e.clientY - startY) / z / innerH) * 100;
      setTables(prev => prev.map(t => {
        if (!ids.includes(t.id)) return t;
        const orig = origPositions[t.id]; if (!orig) return t;
        if (isZoneMode) {
          return { ...t, zone_pos_x: snapVal(Math.max(0, Math.min(90, orig.x+dxPct)), false), zone_pos_y: snapVal(Math.max(0, Math.min(90, orig.y+dyPct)), false) };
        }
        return { ...t, pos_x: snapVal(Math.max(0, Math.min(90, orig.x+dxPct)), false), pos_y: snapVal(Math.max(0, Math.min(90, orig.y+dyPct)), false) };
      }));
      return;
    }
    // Pan drag
    if (panDrag.current) {
      const { startX, startY, origX, origY } = panDrag.current;
      setPan({ x: origX + (e.clientX-startX), y: origY + (e.clientY-startY) });
      return;
    }
    // Box select
    if (selBoxStart.current && selBox) {
      setSelBox(prev => prev ? { ...prev, curX: e.clientX, curY: e.clientY } : null);
    }
  }, [selBox]);

  // ── Mouse up ──────────────────────────────────────────────────────────────────
  const handleMouseUp = useCallback((e) => {
    if (dragging.current && gridSnap) {
      const isZoneMode = dragging.current.isZoneMode;
      setTables(prev => prev.map(t => isZoneMode
        ? { ...t, zone_pos_x: snapVal(t.zone_pos_x, true), zone_pos_y: snapVal(t.zone_pos_y, true) }
        : { ...t, pos_x: snapVal(t.pos_x, true), pos_y: snapVal(t.pos_y, true) }
      ));
    }
    dragging.current = null;
    panDrag.current  = null;

    // Finalize box select
    if (selBoxStart.current && selBox) {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (rect) {
        const z  = zoomRef.current;
        const p  = panRef.current;
        const iw = rect.width; const ih = rect.height;
        const toCanvas = (sx, sy) => ({ x: (sx - rect.left - p.x) / z / iw * 100, y: (sy - rect.top - p.y) / z / ih * 100 });
        const tl = toCanvas(Math.min(selBox.startX, selBox.curX), Math.min(selBox.startY, selBox.curY));
        const br = toCanvas(Math.max(selBox.startX, selBox.curX), Math.max(selBox.startY, selBox.curY));
        const inBox = displayTables.filter(t => {
          const cx = (t._dpx ?? t.pos_x ?? 0) + (t.width||9)/2;
          const cy = (t._dpy ?? t.pos_y ?? 0) + (t.height||9)/2;
          return cx >= tl.x && cx <= br.x && cy >= tl.y && cy <= br.y;
        });
        setSelectedIds(inBox.map(t => t.id));
        if (inBox.length === 1) setSelectedId(inBox[0].id);
        else if (inBox.length === 0) { setSelectedId(null); }
      }
    }
    selBoxStart.current = false;
    setSelBox(null);
  }, [selBox, gridSnap, displayTables]);

  // ── Canvas mousedown ──────────────────────────────────────────────────────────
  const handleCanvasMouseDown = useCallback((e) => {
    const isEmptySpace = e.target === canvasRef.current || e.target === wrapRef.current;
    // Middle button → pan
    if (e.button === 1) {
      e.preventDefault();
      panDrag.current = { startX: e.clientX, startY: e.clientY, origX: panRef.current.x, origY: panRef.current.y };
      return;
    }
    if (e.button === 0 && isEmptySpace) {
      if (mode === 'edit') {
        setSelectedId(null); setSelectedIds([]);
        selBoxStart.current = true;
        setSelBox({ startX: e.clientX, startY: e.clientY, curX: e.clientX, curY: e.clientY });
      }
      // In status/preview: left-drag also pans
      if (mode !== 'edit') {
        panDrag.current = { startX: e.clientX, startY: e.clientY, origX: panRef.current.x, origY: panRef.current.y };
      }
    }
  }, [mode]);

  // ── Table drag start ──────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, id) => {
    if (mode !== 'edit') return;
    const ids = selectedIds.includes(id) ? selectedIds : [id];
    dragging.current = {
      startX: e.clientX, startY: e.clientY,
      ids,
      isZoneMode: !!zoneFilter,
      origPositions: Object.fromEntries(
        ids.map(sid => {
          const t = tables.find(x => x.id===sid);
          return [sid, zoneFilter
            ? { x: t?.zone_pos_x ?? t?.pos_x ?? 0, y: t?.zone_pos_y ?? t?.pos_y ?? 0 }
            : { x: t?.pos_x||0, y: t?.pos_y||0 }
          ];
        })
      ),
    };
  }, [mode, selectedIds, tables, zoneFilter]);

  // ── Touch handlers ────────────────────────────────────────────────────────────
  const handleTouchDragStart = useCallback((touch, id) => {
    const ids = selectedIds.includes(id) ? selectedIds : [id];
    touchRef.current = {
      type: 'tableDrag',
      startX: touch.clientX, startY: touch.clientY,
      ids,
      isZoneMode: !!zoneFilter,
      origPositions: Object.fromEntries(
        ids.map(sid => {
          const t = tables.find(x => x.id === sid);
          return [sid, zoneFilter
            ? { x: t?.zone_pos_x ?? t?.pos_x ?? 0, y: t?.zone_pos_y ?? t?.pos_y ?? 0 }
            : { x: t?.pos_x||0, y: t?.pos_y||0 }
          ];
        })
      ),
    };
  }, [selectedIds, tables, zoneFilter]);

  const handleCanvasTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      touchRef.current = {
        type: 'pan',
        startX: t.clientX, startY: t.clientY,
        origPanX: panRef.current.x, origPanY: panRef.current.y,
      };
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(e.touches[1].clientX - e.touches[0].clientX, e.touches[1].clientY - e.touches[0].clientY);
      touchRef.current = { type: 'pinch', lastDist: dist };
    }
  }, []);

  const handleCanvasTouchMove = useCallback((e) => {
    e.preventDefault();
    const ts = touchRef.current;
    if (!ts.type) return;

    if (ts.type === 'tableDrag' && e.touches.length === 1) {
      const t = e.touches[0];
      const rect = wrapRef.current?.getBoundingClientRect(); if (!rect) return;
      const z = zoomRef.current;
      const dx = t.clientX - ts.startX;
      const dy = t.clientY - ts.startY;
      const dxPct = (dx / z / rect.width) * 100;
      const dyPct = (dy / z / rect.height) * 100;
      setTables(prev => prev.map(tb => {
        if (!ts.ids.includes(tb.id)) return tb;
        const orig = ts.origPositions[tb.id]; if (!orig) return tb;
        if (ts.isZoneMode) {
          return { ...tb, zone_pos_x: Math.max(0, Math.min(90, orig.x + dxPct)), zone_pos_y: Math.max(0, Math.min(90, orig.y + dyPct)) };
        }
        return { ...tb, pos_x: Math.max(0, Math.min(90, orig.x + dxPct)), pos_y: Math.max(0, Math.min(90, orig.y + dyPct)) };
      }));
    } else if (ts.type === 'pan' && e.touches.length === 1) {
      const t = e.touches[0];
      setPan({ x: ts.origPanX + (t.clientX - ts.startX), y: ts.origPanY + (t.clientY - ts.startY) });
    } else if (ts.type === 'pinch' && e.touches.length === 2) {
      const dist = Math.hypot(e.touches[1].clientX - e.touches[0].clientX, e.touches[1].clientY - e.touches[0].clientY);
      const rect = wrapRef.current?.getBoundingClientRect(); if (!rect) return;
      const z = zoomRef.current;
      const newZoom = Math.max(0.25, Math.min(5, z * dist / ts.lastDist));
      const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
      const p = panRef.current;
      const ix = (cx - p.x) / z;
      const iy = (cy - p.y) / z;
      setZoom(newZoom);
      setPan({ x: cx - ix * newZoom, y: cy - iy * newZoom });
      touchRef.current.lastDist = dist;
    }
  }, []);

  const handleCanvasTouchEnd = useCallback(() => {
    touchRef.current = { type: null };
  }, []);

  // Attach touchmove with { passive: false } so preventDefault() works for pinch/pan
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener('touchmove', handleCanvasTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', handleCanvasTouchMove);
  }, [handleCanvasTouchMove]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────────
  useEffect(() => {
    const fn = (e) => {
      if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) return;
      if ((e.key==='Delete'||e.key==='Backspace') && mode==='edit') { e.preventDefault(); deleteTable(); }
      if (e.key==='d' && (e.ctrlKey||e.metaKey)) { e.preventDefault(); duplicateTable(); }
      if (['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key) && mode==='edit') {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 1;
        const dx = e.key==='ArrowLeft'?-step : e.key==='ArrowRight'?step : 0;
        const dy = e.key==='ArrowUp'  ?-step : e.key==='ArrowDown' ?step : 0;
        const ids = selectedIds.length ? selectedIds : (selectedId ? [selectedId] : []);
        if (zoneFilter) {
          setTables(prev => prev.map(t => ids.includes(t.id) ? { ...t, zone_pos_x: Math.max(0,Math.min(90,(t.zone_pos_x ?? t.pos_x ?? 0)+dx)), zone_pos_y: Math.max(0,Math.min(90,(t.zone_pos_y ?? t.pos_y ?? 0)+dy)) } : t));
        } else {
          setTables(prev => prev.map(t => ids.includes(t.id) ? { ...t, pos_x: Math.max(0,Math.min(90,(t.pos_x||0)+dx)), pos_y: Math.max(0,Math.min(90,(t.pos_y||0)+dy)) } : t));
        }
      }
      if (e.key==='Escape') { setSelectedId(null); setSelectedIds([]); setQuickMenu(null); }
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [mode, selectedId, selectedIds, deleteTable, duplicateTable, zoneFilter]);

  // ── Add level ─────────────────────────────────────────────────────────────────
  const addLevel = () => {
    const next = Math.max(...allLevels, 0) + 1;
    const names = { ...levelNames, [next]: `${next}-ci Mərtəbə` };
    setLevelNames(names);
    if (activeLocation?.id)
      supabase.from('business_locations').update({ floor_level_names: names }).eq('id', activeLocation.id);
    setCurrentLevel(next);
  };

  // ── Selection box screen rect ─────────────────────────────────────────────────
  const sbRect = selBox ? {
    left:   Math.min(selBox.startX, selBox.curX),
    top:    Math.min(selBox.startY, selBox.curY),
    width:  Math.abs(selBox.curX - selBox.startX),
    height: Math.abs(selBox.curY - selBox.startY),
  } : null;

  // ── Group-related booleans for toolbar ────────────────────────────────────────
  const anySelected = selectedIds.length > 0 || !!selectedId;
  const selectedHasGroup = [...selectedIds, selectedId].some(id => tables.find(t => t.id===id)?.group_id);

  if (loading) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:400 }}>
      <div style={{ width:40, height:40, border:'4px solid #f3f3f3', borderTop:'4px solid #E63950', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
      <p style={{ color:'#64748B', marginTop:12 }}>Yüklənir...</p>
    </div>
  );

  return (
    <div style={{ padding: isMobile?8:isTablet?14:20, maxWidth:1280, margin:'0 auto' }}>

      {/* ── PAGE HEADER ── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14, gap:12, flexWrap:'wrap' }}>
        <div>
          <h1 style={{ margin:0, fontSize:isMobile?17:20, fontWeight:800, color:'#1a1a2e' }}>🗺️ Virtual Floor Plan</h1>
          <p style={{ margin:'4px 0 0', fontSize:13, color:'#64748B' }}>
            {activeLocation?.name || business?.name} · {activeTables.length} masa · {totalSeats} yer ·{' '}
            <span style={{ color:'#10B981', fontWeight:700 }}>{availableCount} boş</span>
            {occupiedCount>0 && <> · <span style={{ color:'#EF4444', fontWeight:700 }}>{occupiedCount} dolu</span></>}
            {reservedCount>0 && <> · <span style={{ color:'#F59E0B', fontWeight:700 }}>{reservedCount} rezerv</span></>}
          </p>
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
          {/* Mode tabs */}
          <div style={{ display:'flex', background:'#F1F5F9', borderRadius:10, padding:3, gap:2 }}>
            {[{k:'edit',l:isMobile?'✏️':'✏️ Redaktə'},{k:'status',l:isMobile?'🎯':'🎯 Status'}].map(m => (
              <button key={m.k} onClick={() => { setMode(m.k); setQuickMenu(null); }}
                style={{ padding:'7px 12px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:700, fontSize:13,
                  background: mode===m.k?'#fff':'transparent', color: mode===m.k?'#1a1a2e':'#64748B',
                  boxShadow: mode===m.k?'0 1px 4px rgba(0,0,0,0.1)':'none' }}>
                {m.l}
              </button>
            ))}
          </div>
          {/* Auto */}
          <button onClick={() => setAutoMode(v => !v)}
            style={{ padding:'7px 12px', borderRadius:10, border:`1.5px solid ${autoMode?'#10B981':'#E2E8F0'}`, cursor:'pointer', fontWeight:700, fontSize:12,
              background: autoMode?'#ECFDF5':'#F8FAFC', color: autoMode?'#10B981':'#94A3B8' }}>
            {autoSyncing?'⏳':autoMode?'🟢 Auto':'⏸ Auto'}
          </button>
          {mode === 'edit' && (
            <button onClick={saveAll} disabled={saving}
              style={{ padding:'7px 16px', borderRadius:10, border:'none', fontWeight:700, fontSize:13, cursor:saving?'wait':'pointer', color:'#fff',
                background: saving?'#94A3B8':saved?'#10B981':'#1a1a2e' }}>
              {saving?'⏳':saved?'✅ Saxlandı':<><FaSave style={{ marginRight:5 }}/>Saxla</>}
            </button>
          )}
        </div>
      </div>

      {/* ── FLOOR LEVEL BAR ── */}
      <div style={{ display:'flex', gap:6, marginBottom:8, alignItems:'center', flexWrap:'wrap' }}>
        <span style={{ fontSize:12, color:'#64748B', fontWeight:600, flexShrink:0 }}>Mərtəbə:</span>
        {allLevels.map(lv => (
          <button key={lv} onClick={() => setCurrentLevel(lv)}
            style={{ padding:'5px 14px', borderRadius:20, border:'none', cursor:'pointer', fontWeight:700, fontSize:12,
              background: currentLevel===lv?'#1a1a2e':'#F1F5F9', color: currentLevel===lv?'#fff':'#64748B' }}>
            {levelNames[lv]||`${lv}. Mərtəbə`}
            <span style={{ fontSize:10, marginLeft:5, opacity:0.65 }}>({tables.filter(t=>(t.floor_level||1)===lv&&t.is_active).length})</span>
          </button>
        ))}
        {mode==='edit' && (
          <button onClick={addLevel}
            style={{ padding:'5px 12px', borderRadius:20, border:'1.5px dashed #CBD5E1', background:'none', color:'#94A3B8', cursor:'pointer', fontWeight:700, fontSize:12 }}>
            + Mərtəbə əlavə et
          </button>
        )}
      </div>

      {/* ── ZONE TAB BAR ── */}
      {(() => {
        const levelTableCount = tables.filter(t => (t.floor_level||1) === currentLevel && t.is_active).length;
        const zonesInLevel = [...new Set(tables.filter(t => (t.floor_level||1) === currentLevel && t.is_active).map(t => t.zone))];
        return (
          <div style={{ display:'flex', gap:5, marginBottom:12, alignItems:'center', flexWrap:'wrap', overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
            <span style={{ fontSize:12, color:'#64748B', fontWeight:600, flexShrink:0 }}>Zona:</span>
            {/* "Ümumi Zona" tab — always first */}
            <button onClick={() => {
              const restored = prevLevelPlanModeRef.current ?? (levelImages[currentLevel] ? 'image' : 'editor');
              prevLevelPlanModeRef.current = null;
              setZoneFilter(null);
              setPlanModes(prev => ({ ...prev, [currentLevel]: restored }));
            }}
              style={{ padding:'5px 14px', borderRadius:20, cursor:'pointer', fontWeight:700, fontSize:12, flexShrink:0,
                border: !zoneFilter ? '2px solid #1a1a2e' : '1.5px solid #CBD5E1',
                background: !zoneFilter ? '#1a1a2e' : '#F8FAFC',
                color: !zoneFilter ? '#fff' : '#94A3B8' }}>
              🗺️ Ümumi
              <span style={{ fontSize:10, marginLeft:5, opacity:0.7 }}>({levelTableCount})</span>
            </button>
            {/* Divider before sub-zones */}
            {zonesInLevel.length > 0 && (
              <div style={{ width:1, height:20, background:'#E5E7EB', flexShrink:0 }} />
            )}
            {/* Per-zone (sub-zone) tabs */}
            {zonesInLevel.map(zk => {
              const zd = allZones.find(z => z.key === zk);
              const cnt = tables.filter(t => (t.floor_level||1) === currentLevel && t.is_active && t.zone === zk).length;
              const isActive = zoneFilter === zk;
              const hasImg = !!zoneImages[zk];
              return (
                <button key={zk}
                  onClick={() => {
                    // Save current level planMode so Ümumi can restore it exactly
                    if (!zoneFilter) prevLevelPlanModeRef.current = planModes[currentLevel] || 'editor';
                    setZoneFilter(zk);
                    setPlanModes(prev => ({ ...prev, [currentLevel]: hasImg ? 'image' : 'editor' }));
                  }}
                  style={{ padding:'5px 14px', borderRadius:20, cursor:'pointer', fontWeight:700, fontSize:12, flexShrink:0,
                    border: `1.5px solid ${isActive ? (zd?.color||'#8B5CF6') : '#E5E7EB'}`,
                    background: isActive ? (zd?.color||'#8B5CF6') : '#fff',
                    color: isActive ? '#fff' : (zd?.color||'#64748B') }}>
                  {hasImg && <span style={{ marginRight:3 }}>🖼</span>}
                  {zd?.label || zk}
                  <span style={{ fontSize:10, marginLeft:5, opacity: isActive ? 0.8 : 0.65 }}>({cnt})</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* ── EDIT TOOLBAR ── */}
      {mode === 'edit' && (
        <div style={{ display:'flex', gap:8, marginBottom:10, alignItems:'center', flexWrap: isMobile ? 'nowrap' : 'wrap', overflowX: isMobile ? 'auto' : 'visible', background:'#F8FAFC', borderRadius:12, padding:'10px 14px', border:'1.5px solid #F1F5F9', WebkitOverflowScrolling:'touch' }}>
          {/* Plan mode — collapsible */}
          {showPlanModes ? (
            <>
              <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                {[{k:'editor',l:'⬜ Redaktor'},{k:'image',l:'🖼️ Şəkil'},{k:'simple',l:'📋 Sadə'}].map(pm => (
                  <button key={pm.k} onClick={() => savePlanMode(pm.k)}
                    style={{ padding:'6px 11px', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:12,
                      border:`1.5px solid ${currentPlanMode===pm.k?'#1a1a2e':'#E5E7EB'}`,
                      background: currentPlanMode===pm.k?'#1a1a2e':'#fff',
                      color: currentPlanMode===pm.k?'#fff':'#64748B' }}>
                    {pm.l}
                  </button>
                ))}
                <button onClick={() => setShowPlanModes(false)} style={{ padding:'4px 8px', borderRadius:6, border:'none', background:'none', color:'#94A3B8', cursor:'pointer', fontSize:13 }}>✕</button>
              </div>
              <div style={{ width:1, height:24, background:'#E5E7EB', flexShrink:0 }} />
            </>
          ) : (
            <>
              <button onClick={() => setShowPlanModes(true)}
                style={{ padding:'6px 10px', borderRadius:8, border:'1.5px solid #E5E7EB', background:'#fff', color:'#94A3B8', cursor:'pointer', fontWeight:600, fontSize:12 }}>
                ⚙️ Görünüş
              </button>
              <div style={{ width:1, height:24, background:'#E5E7EB', flexShrink:0 }} />
            </>
          )}

          {currentPlanMode !== 'simple' && <>
            <button onClick={() => addTable()} style={TB('#E63950')}><FaPlus/>{isMobile ? '' : ' Masa'}</button>
            {selectedId && <button onClick={duplicateTable} style={TB2}><FaCopy/>{isMobile ? '' : ' Kopyala'}</button>}
            {anySelected && <button onClick={deleteTable} style={TB('#EF4444', '#FEF2F2', '#FECACA')}><FaTrash/>{isMobile ? '' : ` ${selectedIds.length>1?`${selectedIds.length} Sil`:'Sil'}`}</button>}
            {selectedIds.length >= 2 && <button onClick={mergeTables} style={TB('#8B5CF6','#F5F3FF','#DDD6FE')}>🔗{isMobile ? '' : ' Birləşdir'}</button>}
            {anySelected && selectedHasGroup && <button onClick={unmergeTables} style={TB2}>🔓{isMobile ? '' : ' Ayır'}</button>}
            <div style={{ width:1, height:24, background:'#E5E7EB', flexShrink:0 }} />
          </>}

          {/* Grid snap */}
          <button onClick={() => setGridSnap(v=>!v)}
            style={{ padding:'6px 11px', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:12,
              border:`1.5px solid ${gridSnap?'#3B82F6':'#E5E7EB'}`,
              background: gridSnap?'#EFF6FF':'#fff', color: gridSnap?'#3B82F6':'#94A3B8' }}>
            {gridSnap?'📐 Snap ON':'📐 Snap'}
          </button>

          {/* Zone manager toggle */}
          <button onClick={() => setShowZoneMgr(v=>!v)}
            style={{ padding:'6px 11px', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:12,
              border:`1.5px solid ${showZoneMgr?'#8B5CF6':'#E5E7EB'}`,
              background: showZoneMgr?'#F5F3FF':'#fff', color: showZoneMgr?'#8B5CF6':'#94A3B8' }}>
            🏷️ Zonalar
          </button>

          {/* Image tools */}
          <label style={{ ...TB2, cursor:'pointer' }}>
            <FaUpload style={{ marginRight:4 }}/>{uploading?'⏳':'Şəkil'}
            <input type="file" accept="image/*" style={{ display:'none' }} onChange={async (e) => {
              await handleImageUpload(e);
              setPlanModes(prev => ({ ...prev, [currentLevel]: 'image' }));
            }} />
          </label>
          {currentPlanMode === 'image' && <>
            {currentImage && (
              <button onClick={handleImageRemove} style={TB('#EF4444', '#FEF2F2', '#FECACA')}>
                <FaTrash style={{ marginRight:4 }}/>Şəkli Sil
              </button>
            )}
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:11, color:'#64748B', whiteSpace:'nowrap' }}>Şəffaflıq:</span>
              <input type="range" min={20} max={100} value={Math.round(imageOpacity*100)}
                onChange={e => setImageOpacity(parseInt(e.target.value)/100)}
                style={{ width:70, accentColor:'#f97316' }} />
              <span style={{ fontSize:11, color:'#64748B', minWidth:28 }}>{Math.round(imageOpacity*100)}%</span>
            </div>
          </>}
        </div>
      )}

      {/* ── STATUS TOOLBAR ── */}
      {mode === 'status' && (
        <div style={{ display:'flex', gap:8, marginBottom:10, alignItems:'center', flexWrap:'wrap', background:'#F8FAFC', borderRadius:12, padding:'10px 14px' }}>
          {!isMobile && <span style={{ fontSize:12, color:'#94A3B8' }}>Masaya tap et → status dəyiş</span>}
          {confirmResetAll ? (
            <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6, background:'#FEF2F2', border:'1.5px solid #EF4444', borderRadius:8, padding:'4px 10px' }}>
              <span style={{ fontSize:12, color:'#EF4444', fontWeight:700 }}>Əminsiniz?</span>
              <button onClick={() => { resetAllAvailable(); setConfirmResetAll(false); }}
                style={{ padding:'3px 10px', borderRadius:6, border:'none', background:'#EF4444', color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer' }}>Bəli</button>
              <button onClick={() => setConfirmResetAll(false)}
                style={{ padding:'3px 8px', borderRadius:6, border:'none', background:'#fff', color:'#64748B', fontWeight:700, fontSize:12, cursor:'pointer' }}>Xeyr</button>
            </div>
          ) : (
            <button onClick={() => setConfirmResetAll(true)} style={{ marginLeft:'auto', padding:'5px 12px', borderRadius:8, border:'1.5px solid #10B981', background:'#F0FDF4', color:'#10B981', cursor:'pointer', fontWeight:700, fontSize:12 }}>
              ✓ Hamısı Boş
            </button>
          )}
          <button onClick={() => openBookingModal()} style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:8, border:'1.5px solid #6366F1', background:'#EEF2FF', color:'#6366F1', cursor:'pointer', fontWeight:700, fontSize:12 }}>
            <FaCalendarPlus style={{ fontSize:11 }} /> Yeni Rezervasiya
          </button>
        </div>
      )}

      {/* ── ZONE MANAGER ── */}
      {mode === 'edit' && showZoneMgr && (
        <div style={{ background:'#FAFAFA', borderRadius:12, padding:'14px 16px', marginBottom:10, border:'1.5px solid #E5E7EB' }}>

          {/* ── Section 1: Yeni alt zona yarat ── */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:4 }}>🏷️ Yeni alt zona yarat</div>
            <div style={{ fontSize:11, color:'#94A3B8', marginBottom:10 }}>
              Ümumi planı alt zonalara böl — hər zona öz müstəqil görünüşünə sahib olacaq
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <input
                  value={newZoneName}
                  onChange={e => setNewZoneName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addCustomZone()}
                  placeholder="Alt zona adı (məs: Terras, VIP...)"
                  style={{ padding:'6px 10px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, background:'#fff', outline:'none', minWidth:200 }}
                />
                <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                  {CUSTOM_ZONE_COLORS.map(c => (
                    <div key={c} onClick={() => setNewZoneColor(c)}
                      style={{ width:18, height:18, borderRadius:'50%', background:c, cursor:'pointer', outline: newZoneColor===c ? `2.5px solid ${c}` : 'none', outlineOffset:2 }} />
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <span style={{ fontSize:11, color:'#94A3B8', fontWeight:600, flexShrink:0 }}>İkon:</span>
                {ZONE_ICONS.map(ic => (
                  <div key={ic.key} onClick={() => setNewZoneIcon(ic.key)} title={ic.label}
                    style={{ width:30, height:30, borderRadius:8, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16,
                      background: newZoneIcon===ic.key ? newZoneColor+'22' : '#F1F5F9',
                      border: `1.5px solid ${newZoneIcon===ic.key ? newZoneColor : '#E5E7EB'}`, transition:'all 0.12s' }}>
                    {ic.emoji}
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {newZoneName.trim() && (
                  <div style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:20, border:`1.5px solid ${newZoneColor}`, background:newZoneColor+'18', fontSize:12, fontWeight:700, color:newZoneColor }}>
                    <span>{ZONE_ICONS.find(i=>i.key===newZoneIcon)?.emoji}</span>
                    <span>{newZoneName.trim()}</span>
                  </div>
                )}
                <button onClick={addCustomZone} disabled={!newZoneName.trim()}
                  style={{ padding:'6px 14px', borderRadius:8, border:'none', background: newZoneName.trim()?newZoneColor:'#E5E7EB', color: newZoneName.trim()?'#fff':'#94A3B8', fontWeight:700, fontSize:12, cursor: newZoneName.trim()?'pointer':'default' }}>
                  + Alt zona əlavə et
                </button>
              </div>
            </div>
          </div>

          {/* ── Divider ── */}
          <div style={{ borderTop:'1.5px solid #E5E7EB', margin:'0 0 14px' }} />

          {/* ── Section 2: Mövcud zonalar — şəkil idarəsi ── */}
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:4 }}>🖼️ Zona fon şəkilləri</div>
            <div style={{ fontSize:11, color:'#94A3B8', marginBottom:10 }}>
              Hər zona üçün ayrıca fon şəkli yüklə — o zonaya keçəndə həmin şəkil görünəcək
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {allZones.map(z => {
                const img = zoneImages[z.key];
                const isUploading = zoneUploading === z.key;
                const isPreset = PRESET_ZONES.some(p => p.key === z.key);
                return (
                  <div key={z.key} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10, background:'#fff', border:'1.5px solid #E5E7EB' }}>
                    <div style={{ padding:'4px 10px', borderRadius:20, border:`1.5px solid ${z.color}`, background:z.bg, fontSize:12, fontWeight:700, color:z.color, flexShrink:0, minWidth:70, textAlign:'center' }}>
                      {z.label}
                    </div>
                    {img ? (
                      <div style={{ position:'relative', flexShrink:0 }}>
                        <img src={img} alt={z.label} style={{ width:72, height:48, objectFit:'cover', borderRadius:7, border:'1.5px solid #E5E7EB' }} />
                        <button onClick={() => handleZoneImageRemove(z.key)}
                          style={{ position:'absolute', top:-5, right:-5, width:18, height:18, borderRadius:'50%', border:'none', background:'#EF4444', color:'#fff', fontSize:11, fontWeight:900, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}>×</button>
                      </div>
                    ) : (
                      <div style={{ width:72, height:48, borderRadius:7, border:'1.5px dashed #CBD5E1', background:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <span style={{ fontSize:10, color:'#CBD5E1', fontWeight:600 }}>Şəkil yox</span>
                      </div>
                    )}
                    <label style={{ cursor:'pointer', flexShrink:0 }}>
                      <div style={{ padding:'5px 12px', borderRadius:8, border:`1.5px solid ${z.color}`, background:z.bg, color:z.color, fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                        {isUploading ? '⏳ Yüklənir...' : img ? '🔄 Dəyiş' : '📷 Şəkil yüklə'}
                      </div>
                      <input type="file" accept="image/*" style={{ display:'none' }} disabled={!!zoneUploading}
                        onChange={e => { if(e.target.files[0]) handleZoneImageUpload(z.key, e.target.files[0]); e.target.value=''; }} />
                    </label>
                    {!isPreset && (
                      <button onClick={() => deleteCustomZone(z.key)}
                        style={{ marginLeft:'auto', padding:'5px 8px', borderRadius:8, border:'none', background:'#FEF2F2', color:'#EF4444', fontWeight:700, fontSize:12, cursor:'pointer' }}>
                        🗑️ Sil
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      )}

      {/* ── ZOOM CONTROLS ── */}
      {currentPlanMode !== 'simple' && (
        <div style={{ display:'flex', gap:5, marginBottom:8, alignItems:'center' }}>
          <button onClick={() => { const nz=Math.min(5,zoom*1.25); const r=wrapRef.current?.getBoundingClientRect(); if(r){const cx=r.width/2,cy=r.height/2;const ix=(cx-panRef.current.x)/zoomRef.current;const iy=(cy-panRef.current.y)/zoomRef.current;setZoom(nz);setPan({x:cx-ix*nz,y:cy-iy*nz});} }} style={ZB}>+</button>
          <button onClick={() => { const nz=Math.max(0.25,zoom/1.25); const r=wrapRef.current?.getBoundingClientRect(); if(r){const cx=r.width/2,cy=r.height/2;const ix=(cx-panRef.current.x)/zoomRef.current;const iy=(cy-panRef.current.y)/zoomRef.current;setZoom(nz);setPan({x:cx-ix*nz,y:cy-iy*nz});} }} style={ZB}>−</button>
          <button onClick={resetZoom} style={{ ...ZB, minWidth:52, fontSize:11, color:'#64748B' }}>{Math.round(zoom*100)}% ↺</button>
        </div>
      )}

      {/* ── ZONE FILTER BANNER ── */}
      {zoneFilter && (() => {
        const zf = allZones.find(z => z.key === zoneFilter);
        return (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', borderRadius:10, background: zf?.bg || '#F5F3FF', border:`1.5px solid ${zf?.color || '#8B5CF6'}`, marginBottom:10 }}>
            <div>
              <span style={{ fontWeight:800, fontSize:13, color: zf?.color || '#8B5CF6' }}>📍 {zf?.label || zoneFilter} alt zonası</span>
              <span style={{ fontSize:12, color:'#64748B', marginLeft:8 }}>Ümumi planın bu hissəsini görürsünüz. Masalar bu zonaya ayrılıb.</span>
            </div>
            <button onClick={() => {
              const restored = prevLevelPlanModeRef.current ?? (levelImages[currentLevel] ? 'image' : 'editor');
              prevLevelPlanModeRef.current = null;
              setZoneFilter(null);
              setPlanModes(prev => ({ ...prev, [currentLevel]: restored }));
            }}
              style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:8, border:'1.5px solid #1a1a2e', background:'#1a1a2e', color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer', flexShrink:0 }}>
              🗺️ Ümumi Zonaya qayıt
            </button>
          </div>
        );
      })()}

      {/* ── MAIN BODY ── */}
      <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>

        {/* ── CANVAS AREA ── */}
        <div style={{ flex:1, minWidth:0 }}>

          {/* Simple zone view */}
          {currentPlanMode === 'simple' && (
            <SimpleZoneView tables={levelTables} mode={mode}
              onStatusChange={handleStatusChange} onAddTable={addTable}
              onQuickMenu={handleQuickMenu} groupColors={groupColorMap} timerNow={timerNow} allZones={allZones} />
          )}

          {/* Zoom/pan canvas */}
          {currentPlanMode !== 'simple' && (
            <div ref={wrapRef}
              style={{ position:'relative', overflow:'hidden', borderRadius:14, height:canvasH, border:'1.5px solid #E5E7EB', background:'#FAFAFA', cursor: mode==='edit'?'crosshair':panDrag.current?'grabbing':'grab', touchAction:'none', userSelect:'none' }}
              onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp} onMouseDown={handleCanvasMouseDown}
              onTouchStart={handleCanvasTouchStart} onTouchEnd={handleCanvasTouchEnd}
            >
              {/* Inner (transformed) canvas */}
              <div ref={canvasRef} data-canvas="1"
                style={{ position:'absolute', left:0, top:0, width:'100%', height:'100%',
                  transformOrigin:'0 0', transform:`translate(${pan.x}px,${pan.y}px) scale(${zoom})`,
                  backgroundImage: mode==='edit'&&currentPlanMode==='editor' ? 'radial-gradient(circle,#CBD5E1 1px,transparent 1px)' : 'none',
                  backgroundSize:'28px 28px' }}
              >
                {/* BG image */}
                {currentPlanMode==='image' && currentImage && (
                  <img src={currentImage} alt="plan" draggable={false}
                    style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'fill', opacity:imageOpacity, pointerEvents:'none' }} />
                )}
                {currentPlanMode==='image' && !currentImage && (
                  <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#CBD5E1' }}>
                    <FaImage style={{ fontSize:48, marginBottom:14 }} />
                    <p style={{ fontWeight:700, color:'#94A3B8' }}>Plan şəkli yüklənməyib</p>
                    <p style={{ fontSize:12 }}>"Şəkil" düyməsindən yükləyin</p>
                  </div>
                )}

                {/* Tables */}
                {displayTables.map(t => (
                  <TableItem key={t.id} table={t}
                    selected={selectedId===t.id} multiSel={selectedIds.includes(t.id)}
                    onSelect={id => { setSelectedId(id); setSelectedIds([]); }}
                    onDragStart={handleDragStart}
                    onTouchDragStart={handleTouchDragStart}
                    mode={mode}
                    onStatusChange={handleStatusChange}
                    scale={scale} groupColor={groupColorMap[t.group_id]}
                    timerNow={timerNow}
                    onQuickMenu={mode==='status' ? handleQuickMenu : null}
                    hasFutureRes={futureResIds.has(t.id)}
                    allZones={allZones}
                  />
                ))}

                {/* Empty state */}
                {levelTables.length===0 && (
                  <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                    <p style={{ color:'#94A3B8', fontWeight:700 }}>Bu mərtəbədə masa yoxdur</p>
                    <p style={{ color:'#CBD5E1', fontSize:12 }}>Yuxarıdan "Masa əlavə et" düyməsinə basın</p>
                  </div>
                )}

                {/* Smart Table Hub */}
                {quickMenu && !isMobile && (
                  <SmartTableHub
                    table={quickMenu.table} x={quickMenu.x} y={quickMenu.y}
                    canvasW={canvasRef.current?.offsetWidth}
                    canvasH={canvasRef.current?.offsetHeight}
                    onAction={handleStatusChange}
                    onClose={() => setQuickMenu(null)}
                    businessId={business?.id}
                    onBook={openBookingModal}
                    isMobile={false}
                  />
                )}

              </div>

              {/* Selection box (fixed coords) */}
              {sbRect && sbRect.width>5 && (
                <div style={{ position:'fixed', left:sbRect.left, top:sbRect.top, width:sbRect.width, height:sbRect.height, border:'2px dashed #3B82F6', background:'rgba(59,130,246,0.07)', pointerEvents:'none', zIndex:9999, borderRadius:4 }} />
              )}

              {/* Zoom hint overlay */}
              {zoom!==1 && (
                <div style={{ position:'absolute', bottom:8, left:8, background:'rgba(0,0,0,0.45)', color:'#fff', borderRadius:8, padding:'3px 8px', fontSize:11, fontWeight:700, pointerEvents:'none' }}>
                  {Math.round(zoom*100)}%
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT PROPS PANEL (desktop) ── */}
        {mode==='edit' && selected && currentPlanMode!=='simple' && !isMobile && (
          <PropsPanel selected={selected} updateField={updateField}
            isMobile={false} isTablet={isTablet}
            groupColor={groupColorMap[selected.group_id]}
            onDelete={deleteTable} onDuplicate={duplicateTable}
            businessId={business?.id}
            onPhotoUpload={handleTablePhotoUpload} photoUploading={photoUploading}
            allZones={allZones} />
        )}
      </div>

      {/* ── MOBILE PROPS (bottom sheet) ── */}
      {isMobile && mode==='edit' && selected && currentPlanMode!=='simple' && (
        <>
          <button onClick={() => setShowProps(p=>!p)}
            style={{ position:'fixed', bottom:20, right:16, zIndex:120, padding:'12px 18px', borderRadius:24, fontWeight:700, fontSize:14, background:showProps?'#1E293B':'#E63950', color:'#fff', border:'none', boxShadow:'0 4px 20px rgba(0,0,0,0.25)', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
            {showProps ? '✕ Bağla' : `⚙ #${selected.table_number}`}
          </button>
          {showProps && <>
            <div onClick={() => setShowProps(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.3)', zIndex:99 }} />
            <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:100, background:'#fff', borderRadius:'16px 16px 0 0', padding:'16px 16px 40px', maxHeight:'82vh', overflowY:'auto', boxShadow:'0 -4px 24px rgba(0,0,0,0.12)' }}>
              <div style={{ textAlign:'center', marginBottom:12 }}>
                <div style={{ width:36, height:4, borderRadius:2, background:'#CBD5E1', display:'inline-block' }} />
              </div>
              <PropsPanel selected={selected} updateField={updateField} isMobile
                groupColor={groupColorMap[selected.group_id]}
                onDelete={() => { deleteTable(); setShowProps(false); }}
                onDuplicate={() => { duplicateTable(); setShowProps(false); }}
                businessId={business?.id}
                onPhotoUpload={handleTablePhotoUpload} photoUploading={photoUploading}
                allZones={allZones} />
            </div>
          </>}
        </>
      )}


      {/* ── UPCOMING BOOKINGS STRIP (status mode) ── */}
      {mode === 'status' && (() => {
        const now = new Date();
        const curMin = now.getHours()*60 + now.getMinutes();
        const toMin  = s => { const [h,m]=(s||'00:00').slice(0,5).split(':').map(Number); return h*60+m; };
        const upcoming = [...allBookings]
          .filter(b => b.table_id && levelTables.find(t => t.id === b.table_id))
          .sort((a,b) => toMin(a.reservation_time) - toMin(b.reservation_time));
        if (upcoming.length === 0) return null;
        return (
          <div style={{ marginTop:14, background:'#F8FAFC', borderRadius:12, padding:'10px 14px', border:'1.5px solid #F1F5F9' }}>
            <div style={{ fontSize:12, color:'#64748B', fontWeight:700, marginBottom:8 }}>
              📋 Bugünkü rezervasiyalar ({upcoming.length})
            </div>
            <div style={{ display:'flex', gap:8, overflowX:'auto', paddingBottom:4, WebkitOverflowScrolling:'touch' }}>
              {upcoming.map(b => {
                const mins  = toMin(b.reservation_time) - curMin;
                const tbl   = levelTables.find(t => t.id === b.table_id);
                const active = mins <= 0 && (b.end_time ? toMin(b.end_time) > curMin : mins > -120);
                const soon   = mins > 0 && mins <= 60;
                return (
                  <div key={b.id} style={{
                    flexShrink:0, background:'#fff', borderRadius:10, padding:'8px 12px',
                    border:`1.5px solid ${active?'#EF4444':soon?'#F59E0B':'#E5E7EB'}`,
                    minWidth:140,
                  }}>
                    <div style={{ fontSize:13, fontWeight:800, color: active?'#EF4444':soon?'#F59E0B':'#1a1a2e' }}>
                      {(b.reservation_time||'').slice(0,5)}{b.end_time && `–${b.end_time.slice(0,5)}`}
                    </div>
                    <div style={{ fontSize:12, color:'#374151', fontWeight:600, marginTop:2 }}>{b.contact_name||'—'}</div>
                    <div style={{ fontSize:11, color:'#94A3B8', marginTop:1 }}>
                      Masa {tbl?.table_number||'?'} · {b.guest_count||'?'} nəf
                    </div>
                    {active && <div style={{ fontSize:10, color:'#EF4444', fontWeight:700, marginTop:3 }}>● Aktiv</div>}
                    {soon && !active && <div style={{ fontSize:10, color:'#F59E0B', fontWeight:700, marginTop:3 }}>⏱ {mins}d sonra</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── MOBILE SMART TABLE HUB (bottom sheet, outside canvas) ── */}
      {quickMenu && isMobile && (
        <SmartTableHub
          table={quickMenu.table} x={0} y={0}
          onAction={handleStatusChange}
          onClose={() => setQuickMenu(null)}
          businessId={business?.id}
          onBook={openBookingModal}
          isMobile={true}
        />
      )}

      {/* ── CONFLICT MODAL ── */}
      {conflictModal && (
        <ConflictModal
          booking={conflictModal.booking}
          onConfirm={() => { applyStatusChange(conflictModal.table, conflictModal.nextStatus); setConflictModal(null); }}
          onCancel={() => setConflictModal(null)}
        />
      )}

      {/* ── MANUAL BOOKING MODAL ── */}
      {showBookingModal && (
        <>
          <div onClick={() => setShowBookingModal(false)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:200 }} />
          <div style={{
            position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
            zIndex:201, background:'#fff', borderRadius:16, padding:24,
            width: isMobile ? 'calc(100vw - 32px)' : 440,
            maxHeight:'90vh', overflowY:'auto',
            boxShadow:'0 20px 60px rgba(0,0,0,0.22)',
          }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
              <h3 style={{ margin:0, fontSize:17, fontWeight:800, color:'#1a1a2e' }}>📅 Yeni Rezervasiya</h3>
              <button onClick={() => setShowBookingModal(false)}
                style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#94A3B8', lineHeight:1 }}>✕</button>
            </div>

            <div style={BF.group}>
              <label style={BF.label}>Masa *</label>
              <select value={bookingForm.table_id} onChange={e => setBookingForm(f => ({ ...f, table_id: e.target.value }))} style={BF.input}>
                <option value="">— Masa seçin —</option>
                {levelTables.filter(t => t.is_active).map(t => (
                  <option key={t.id} value={t.id}>
                    Masa {t.table_number} ({t.capacity} nəf) – {getZone(t.zone, allZones).label}
                  </option>
                ))}
              </select>
            </div>

            <div style={BF.group}>
              <label style={BF.label}>Qonaq adı *</label>
              <input value={bookingForm.contact_name}
                onChange={e => setBookingForm(f => ({ ...f, contact_name: e.target.value }))}
                placeholder="Ad Soyad" style={BF.input} />
            </div>

            <div style={BF.group}>
              <label style={BF.label}>Telefon</label>
              <input value={bookingForm.phone}
                onChange={e => setBookingForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+994..." type="tel" style={BF.input} />
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <div style={{ ...BF.group, flex:1 }}>
                <label style={BF.label}>Tarix *</label>
                <input type="date" value={bookingForm.reservation_date}
                  onChange={e => setBookingForm(f => ({ ...f, reservation_date: e.target.value }))} style={BF.input} />
              </div>
              <div style={{ ...BF.group, flex:1 }}>
                <label style={BF.label}>Başlanğıc *</label>
                <input type="time" value={bookingForm.reservation_time}
                  onChange={e => setBookingForm(f => ({ ...f, reservation_time: e.target.value }))} style={BF.input} />
              </div>
              <div style={{ ...BF.group, flex:1 }}>
                <label style={BF.label}>Bitmə</label>
                <input type="time" value={bookingForm.end_time}
                  onChange={e => setBookingForm(f => ({ ...f, end_time: e.target.value }))} style={BF.input} />
              </div>
            </div>

            <div style={BF.group}>
              <label style={BF.label}>Nəfər sayı</label>
              <input type="number" min={1} max={50} value={bookingForm.guest_count}
                onChange={e => setBookingForm(f => ({ ...f, guest_count: e.target.value }))} style={BF.input} />
            </div>

            <div style={BF.group}>
              <label style={BF.label}>Qeyd</label>
              <textarea value={bookingForm.note}
                onChange={e => setBookingForm(f => ({ ...f, note: e.target.value }))}
                rows={2} placeholder="Xüsusi istəklər..." style={{ ...BF.input, resize:'none', fontFamily:'inherit' }} />
            </div>

            <div style={{ display:'flex', gap:10, marginTop:4 }}>
              <button onClick={() => setShowBookingModal(false)}
                style={{ flex:1, padding:'11px', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', fontWeight:700, fontSize:14, cursor:'pointer', color:'#64748B' }}>
                Ləğv et
              </button>
              <button onClick={submitBooking}
                disabled={bookingSaving || !bookingForm.contact_name?.trim() || !bookingForm.table_id}
                style={{ flex:2, padding:'11px', borderRadius:10, border:'none', fontWeight:700, fontSize:14, cursor: (!bookingForm.contact_name?.trim() || !bookingForm.table_id) ? 'not-allowed' : 'pointer',
                  background: (!bookingForm.contact_name?.trim() || !bookingForm.table_id) ? '#CBD5E1' : '#6366F1', color:'#fff' }}>
                {bookingSaving ? '⏳ Saxlanır...' : '✅ Rezervasiya yarat'}
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

// ── Toolbar button helpers ─────────────────────────────────────────────────────
const TB  = (c, bg, bc) => ({ display:'flex', alignItems:'center', gap:4, padding:'6px 11px', borderRadius:8, border:`1.5px solid ${bc||c}`, background:bg||c, color:bg?c:'#fff', fontWeight:700, fontSize:12, cursor:'pointer', flexShrink:0 });
const TB2 = { display:'flex', alignItems:'center', gap:4, padding:'6px 11px', borderRadius:8, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, fontSize:12, cursor:'pointer', flexShrink:0 };
const ZB  = { padding:'5px 11px', borderRadius:8, border:'1.5px solid #E5E7EB', background:'#fff', cursor:'pointer', fontSize:14, fontWeight:800, color:'#374151' };

// ── Booking form field styles ──────────────────────────────────────────────────
const BF = {
  group: { marginBottom: 14 },
  label: { display:'block', fontSize:12, color:'#64748B', fontWeight:600, marginBottom:5 },
  input: { width:'100%', padding:'9px 11px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, color:'#1a1a2e', background:'#fff', boxSizing:'border-box', outline:'none' },
};
